// ============================================================
// CashFlowReport.jsx — Cash Flow Projection Report
//
// Every deal's projected cash flow on one shared calendar axis, at a
// resolution the reader chooses: monthly, quarterly, half-yearly or yearly.
//
// THE CLIENT MIRRORS NOTHING. Every figure on this page — each period bucket,
// each total, the cumulative line, the exit value netted of debt — comes from
// the server's response (api/src/cashFlowProjection.js). A client-side copy of
// the bucketing would be a second implementation of the one thing this report
// does, and it would drift. The page formats and lays out; it does not compute.
//
// TWO RATES SIT BESIDE THE TOTAL, and they are LABELLED. `unrealized` sells
// every position today at what it is carried at; `projected` holds it to plan
// and takes the modelled exit. Both come from the API (cashFlowIrr.js) and both
// carry the sentence saying what they assumed. One unqualified "IRR" on a
// screen gets quoted as whichever the reader assumed, which is why there are
// two columns rather than one, and why an unsolvable rate is a dash carrying
// the reason rather than "0.0%" — a position with capital out and nothing
// modelled coming back has not broken even, it has not been answered.
//
// THE ROW'S CHIPS ARE GONE, AND THE DEAL NAME EXPLAINS THE ROW INSTEAD. There
// used to be up to five bubbles on a row — which model spoke for it, how much
// was realized, a note's terms, a matured note, an exit borrowed from the mark,
// debt repaid out of a sibling's sale — each compressed to four words with its
// meaning in a `title` attribute nobody hovers by accident. That is a summary of
// a summary. Hovering (or focusing) the deal name now opens a card carrying all
// of it laid out: the model, its components — capital, income, expenses, debt,
// exit — the mark it is carried at, the exit date and where that date came
// from, and every caveat the engine raised. The one bubble left is the CAPITAL
// TYPE (N/R · R/E · Active · Passive), which is the thing worth reading at a
// glance and the thing the card cannot replace.
//
//   * SAGE POSTINGS ARE NOT ENUMERATED IN THAT CARD. It explains the VALUATION
//     METHOD, and a capital item's ledger runs to hundreds of postings — listing
//     them would bury the five figures that answer the question. The card says
//     in one line that realized cash is blended into the series and points at
//     the Transactions tab; the method breakdown above it is plans only.
//
// What else it takes care to SHOW rather than hide:
//
//   * MONEY OUTSIDE THE WINDOW. The axis is capped and can be narrowed to a
//     date range, and the API reports what fell outside as `before` / `after`.
//     Those columns are rendered whenever they are non-zero, so a total can
//     never silently disagree with the row it sits on.
//
//   * WHAT ISN'T PROJECTED, AND WHY. A deal with no budget, no schedule and no
//     dated valuation is listed with the reason in words instead of appearing
//     as a row of zeros — which would quietly drag the portfolio total down.
//
//   * WHICH COLUMNS ARE HISTORY. The report now carries realized Sage postings
//     beside the forecasts, so a row's early columns are fact and its later ones
//     are a plan — and rendering the two in identical ink is precisely how a
//     forecast gets quoted as a result. The periods that close on or before the
//     server's `asOf` are shaded and headed "actual", and the cut-off comes from
//     the API rather than the browser's own clock, which would drift from it
//     across a timezone and mislabel a whole column.
//
//   * DEBT REPAID OUT OF A SIBLING'S SALE. Borrowing is often booked on a
//     separate capital item from the asset it financed, so the API repays it
//     out of the deal's mark-driven sale (see projectDealDetails). The row then
//     shows less than the mark, which reads as an error unless the subtraction
//     is on screen — so it is, in the deal's method card.
//
//   * WHAT THE MARKET DID, BESIDE WHAT THESE DEALS ARE PROJECTED TO DO. The
//     chart asks it the way the market answers it — what would a dollar have
//     done — so all three series are CUMULATIVE GROWTH on one axis: the S&P
//     500's measured levels over a trailing window, and our two rates
//     compounded over the same elapsed span. The deal side is the
//     CAPITAL-WEIGHTED AVERAGE of the deals' rates, which is deliberately not
//     the portfolio rate in the "All deals" row (that solves one rate over
//     every flow in the book), and the card says so. The asymmetry is real and
//     is on screen rather than glossed: the market curve is MEASURED and
//     BACKWARD-LOOKING (the S&P has no future, so there is no forward market
//     rate to compare a 2030 exit against) and FRED's SP500 series is a PRICE
//     index, excluding dividends, so it sits roughly 1.5-2 points below a
//     total-return benchmark — a bias in favour of the deals being compared.
//
// IT OPENS ON THE CURRENT PERIOD, NOT AT THE FAR LEFT. The axis is the span of
// the projected money and routinely starts years in the past, so the first
// screenful was history nobody asked for and every reader began by scrolling.
// The column for today is scrolled to the left edge on load and whenever the
// axis itself changes (a new granularity, a new date window) — but never on an
// ordinary re-render, or expanding a row would yank the table back and undo
// wherever the reader had scrolled to.
// ============================================================
import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { B, font, radius, fonts, weight, space } from '../constants/brand';
import { api } from '../api/client';
import { fmt } from '../utils/format';
// PARKED with runEventExport: import { exportCashFlowEventsXlsx, describeCashFlowExport } from '../utils/exportDownload';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, PageHeader, Icon, Badge, Card } from '../components/ui';
import {
  compactMoney, dealMatchesQuery, currentPeriodIndex, rowIsAllZero, formatRate, rateSign, formatGrowth, growthAxis, stackLabels, splitAtAssumed, yearTicks,
  collectRowNotes,
} from '../utils/cashFlowFormat';
import { DETAIL_TYPES, ENTITY_TYPE, colorOf, labelOf, shortOf } from '../constants/detailTypes';
import { exportCashFlowXlsx, describeCashFlowWorkbook } from '../utils/exportDownload';

// THE READ IS A WHOLE-BOOK RUN, SO IT CANNOT RIDE THE 10s CLIENT DEFAULT.
// One request projects every deal in the book from four engines and reads the
// whole Sage ledger (`loadActuals`), and the export of the SAME computation has
// carried a long timeout since it shipped -- the screen's own read was simply
// overlooked, so the report that the workbook is made from was the one surface
// that could not finish. It failed at exactly 10s on every granularity, which
// is the tell: a bucketing choice costs nothing server-side, so the wall was
// the client's clock rather than the work.
//
// 2 min for the XLSX_TIMEOUT_MS reason (`utils/exportDownload.js`) -- the same
// sources, minus the workbook. It is deliberately ABOVE the ~45s Static Web
// Apps edge window rather than tuned under it: past that the edge answers with
// its own bodyless 500, so whatever the ceiling turns out to be it is the
// edge's to report and not a number we guessed here. A timeout is never
// retried (`fetchRetry`), so this is one attempt, not three.
const REPORT_TIMEOUT_MS = 120000;

// The four resolutions the API offers, with the words a reader expects.
const GRANULARITIES = [
  { value: 'month',   label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'half',    label: 'Half-Year' },
  { value: 'year',    label: 'Yearly' },
];

// THE TABLE AND THE CHART ARE TWO VIEWS OF ONE REPORT, AND ONLY ONE IS ON
// SCREEN AT A TIME. They used to be stacked, which read as a chart with a
// hundred-row table under it rather than as two ways of asking the same
// question — and it put the chart's own y axis and the matrix's period columns
// in the same eyeful, which are different scales of different things.
//
// THE TABLE IS THE DEFAULT, deliberately. It is the report: every caveat, the
// measure and the resolution are about it, and opening on the chart would
// change what everybody sees for the sake of the newer half. The export is a
// property of NEITHER view — it is the event layer, which no view renders —
// so it lives in the header beside Refresh rather than with the controls.
const VIEWS = [
  { value: 'table', label: 'Table' },
  // The 'chart' view now LEADS with the rate comparison and keeps the growth
  // curve behind a link; the key is unchanged so nothing keyed on it moves.
  { value: 'chart', label: 'vs S&P 500' },
];

// THE FORWARD MARKET ASSUMPTION IS A FIXED LIST, NOT A NUMBER BOX.
//
// The API clamps to ±50% and normalizes anything unreadable to its default, so
// a free field can express 500% and a blank and have them silently corrected —
// a control that lies about what it does. (The `harviBrief` hour picker's rule,
// for the same reason.) The empty value is the ENGINE'S default rather than a
// number restated here: a second copy of 6.5% on the client would be the figure
// that goes stale when the engine's changes.
//
// The range is bracketed around the long-run figure rather than around the
// trailing decade, which has been unusually strong — adopting that silently as
// a forecast for the next twenty-five years is a momentum bet wearing a
// measurement's clothes, so it is printed BESIDE the control as context and is
// not the default.
const FORWARD_GROWTH_OPTIONS = [
  { value: '', label: 'Default' },
  { value: '0', label: '0%' },
  { value: '3', label: '3%' },
  { value: '5', label: '5%' },
  { value: '6.5', label: '6.5%' },
  { value: '8', label: '8%' },
  { value: '10', label: '10%' },
  // A HOUSE VIEW ADDED BY NAME, NOT A GAP IN THE LIST WIDENED TO A NUMBER BOX.
  // (7650.5 / 2159.04)^(1/10.02yr) - 1 over 9/12/16-9/18/26 — the trailing S&P
  // price CAGR, adopted deliberately as the forward figure despite the caveat
  // two paragraphs up (it IS the "unusually strong trailing decade" the fixed
  // list was bracketed away from). Still one click through the control below,
  // not a silent default, so the decision and its date stay on the record.
  { value: '13.4547681', label: '13.45% (S&P trailing 10yr, 9/12/16–9/18/26)' },
];

// The API returns all four series per row, so switching measure costs no
// request — and all four are worth having: `net` is the answer, `inflow` /
// `outflow` are what it is made of, and `cumulative` is the one that shows when
// a position is expected to have paid itself back.
const MEASURES = [
  { key: 'net',           label: 'Net' },
  { key: 'inflow',        label: 'Cash in' },
  { key: 'outflow',       label: 'Cash out' },
  { key: 'cumulativeNet', label: 'Cumulative' },
];

// `actual` is listed last and toned differently from the three models on
// purpose: it is not a forecast, and a reader scanning chips should be able to
// tell "this deal has been modelled" from "this deal has a general ledger" at a
// glance. `PROJECTION_SOURCES` mirrors the engine's constant of the same name.
const PROJECTION_SOURCES = ['budget', 'loan', 'schedule', 'valuation'];

const SOURCE_LABELS = {
  budget:    { label: 'Budget',    color: B.navy,   hint: 'Projected from the R/E budget pro forma, including its modelled exit.' },
  loan:      { label: 'Loan',      color: B.accent, hint: 'Projected from the note’s own terms — the advance, every scheduled payment through maturity, and the principal still outstanding when it gets there. Expanded by the same engine that generates the payment records the borrower is billed from.' },
  schedule:  { label: 'Schedule',  color: B.tealDk, hint: 'Projected from the capital call / distribution schedule.' },
  valuation: { label: 'Valuation', color: B.amber,  hint: 'Projected as a sale at the current mark, net of debt outstanding.' },
  actual:    { label: 'Actual',    color: B.green,  hint: 'Realized cash from the Sage general ledger — what has already moved, not a forecast.' },
};

// A money cell. `outflow` is the one measure whose values arrive POSITIVE — the
// API reports outflow as a magnitude so a reader can add the column up — so in
// that view the sign cannot carry the meaning and the cell is toned as an
// outflow regardless. Without this, "$45k" under a "Cash out" heading renders
// in exactly the same ink as "$45k" under "Cash in".
const MoneyCell = ({ value, strong = false, alwaysOut = false }) => {
  const text = compactMoney(value);
  if (!text) return <span style={{ color: B.textLt }}>—</span>;
  const negative = alwaysOut || value < 0;
  return (
    <span style={{
      color: negative ? B.red : (strong ? B.green : B.text),
      fontWeight: strong ? weight.semibold : weight.regular,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    }}>{text}</span>
  );
};

// ---- CSV -------------------------------------------------------------------
// The rows and the escaping are pure (utils/cashFlowFormat.js) and unit-tested;
// ---- small pieces ----------------------------------------------------------

const Segmented = ({ options, value, onChange, ariaLabel }) => (
  <div role="tablist" aria-label={ariaLabel} style={{
    display: 'inline-flex', gap: 3, background: B.surfaceMuted, border: `1px solid ${B.divider}`,
    borderRadius: radius.pill, padding: 3,
  }}>
    {options.map(o => {
      const on = o.value === value || o.key === value;
      const v = o.value ?? o.key;
      return (
        <button key={v} role="tab" aria-selected={on} onClick={() => onChange(v)} style={{
          padding: '6px 13px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', borderRadius: radius.pill,
          fontSize: font.sm, fontWeight: on ? weight.semibold : weight.medium,
          color: on ? B.navyDk : B.textMd, background: on ? B.surface : 'transparent',
          boxShadow: on ? B.shadowXs : 'none', whiteSpace: 'nowrap',
        }}>{o.label}</button>
      );
    })}
  </div>
);

// ---- the filter bar ---------------------------------------------------------
//
// ONE TOOLBAR, ONE EXPANDABLE FILTER PANEL, AND THE SPLIT IS THE POINT.
// The toolbar carries the view toggle and the controls that decide HOW THE RUN
// IS SHOWN — resolution, measure, the date window and the empty-row switch for
// the table; the forward market assumption for the chart. Those are touched on
// every visit, so they stay in view; a control that does nothing to what is on
// screen is not rendered, and its state is KEPT so switching views lands back
// on the table that was left.
//
// Everything that decides WHAT IS IN THE RUN — and therefore reaches the server
// and moves every total, both IRRs, the chart and the workbook alike — sits in
// a "Filters" panel that opens on demand: status, the capital types, the
// OWNER entities (one checkbox PER ENTITY — the Sage entity at the root of the
// location hierarchy above a deal), the R/E hold types. A
// row of toggle pills for all of that at once read as clutter — and worse, a
// pill did not say which way it was set, where a checkbox does. The panel is
// CLOSED by default; its header always names what is in force, each with its
// own clear, so a narrowed run can never hide behind a closed panel — the one
// rule the old always-visible bar existed for, kept in a smaller footprint.
//
// "Clear filters" resets the FILTERS and nothing else. Resolution, measure and
// Hide 0 rows are how the table is read, not which deals are in it, and a
// button that reset them alongside would change the rendering of a report the
// reader had not asked to change.
const FilterGroup = ({ label, children, hint }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} title={hint || undefined}>
    <span style={{
      fontSize: font.xs, fontWeight: weight.semibold, color: B.textLt, textTransform: 'uppercase',
      letterSpacing: 0.4, whiteSpace: 'nowrap',
    }}>{label}</span>
    {children}
  </div>
);

const FilterTier = ({ label, children }) => (
  <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center' }}>
    <span style={{
      fontSize: font.xs, color: B.textLt, fontStyle: 'italic', whiteSpace: 'nowrap', minWidth: 74,
    }}>{label}</span>
    {children}
  </div>
);

// One labelled row of the filter panel: a fixed-width label so the groups
// line up, and top-aligned so a list that wraps to several lines (the Sage
// entities) keeps its label on its first line.
const PanelRow = ({ label, hint, children }) => (
  <div style={{ display: 'flex', gap: space.md, alignItems: 'flex-start', minWidth: 0 }} title={hint || undefined}>
    <span style={{
      fontSize: font.xs, fontWeight: weight.semibold, color: B.textLt, textTransform: 'uppercase',
      letterSpacing: 0.4, whiteSpace: 'nowrap', minWidth: 112, lineHeight: '22px', flex: 'none',
    }}>{label}</span>
    <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: space.md, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {children}
    </div>
  </div>
);

const linkBtn = {
  border: 'none', background: 'none', cursor: 'pointer', color: B.navy, padding: 0,
  fontFamily: 'inherit', fontSize: font.xs, textDecoration: 'underline',
};

// `items`: [{ key, label, onClear }] — one per filter in force. Renders nothing
// when there is none, so an unfiltered run carries no "no filters" line. It
// sits in the disclosure HEADER, beside the Filters button, so it reads whether
// the panel is open or closed.
const ActiveFilters = ({ items, onClearAll }) => {
  if (!items.length) return null;
  return (
    <div role="status" aria-label="Filters in force" style={{
      display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: font.xs, color: B.textMd,
    }}>
      <span style={{ fontWeight: weight.semibold, color: B.textLt, textTransform: 'uppercase', letterSpacing: 0.4 }}>In force</span>
      {items.map((it) => (
        <span key={it.key} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 8px',
          border: `1px solid ${B.border}`, borderRadius: radius.pill, background: B.surface,
        }}>
          {it.label}
          <button type="button" onClick={it.onClear} aria-label={`Clear ${it.label}`} title={`Clear: ${it.label}`} style={{
            border: 'none', background: 'none', cursor: 'pointer', color: B.textLt, padding: '0 3px',
            fontFamily: 'inherit', fontSize: font.sm, lineHeight: 1,
          }}>×</button>
        </span>
      ))}
      {items.length > 1 && (
        <button type="button" onClick={onClearAll} style={{ ...linkBtn, padding: '2px 4px' }}>Clear filters</button>
      )}
    </div>
  );
};

// THE EXPANDABLE PANEL. A real button carrying aria-expanded / aria-controls
// (a chevron span alone is reachable by nobody on a keyboard), a count of the
// filters in force so a closed panel still says it is narrowing something, and
// the in-force summary beside it — the header is the part that is always on
// screen, so it is where the truth about the run has to live.
const FilterDisclosure = ({ open, onToggle, count, summary, children }) => (
  <div style={{ borderTop: `1px dashed ${B.divider}`, paddingTop: space.sm }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="cf-filter-panel"
        onClick={onToggle}
        title={open ? 'Hide the filters' : 'Show the filters: status, capital types, owner entities, hold types'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px 5px 8px',
          border: `1px solid ${open ? B.navy : B.border}`, borderRadius: radius.md,
          background: B.surface, color: B.navyDk, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: font.sm, fontWeight: weight.semibold,
        }}
      >
        <span aria-hidden="true" style={{
          display: 'inline-block', fontSize: font.xs, transition: 'transform .15s',
          transform: open ? 'rotate(90deg)' : 'none',
        }}>▶</span>
        Filters
        {count > 0 && (
          <span aria-label={`${count} in force`} style={{
            minWidth: 18, height: 18, padding: '0 5px', borderRadius: radius.pill, background: B.navy, color: '#fff',
            fontSize: 11, fontWeight: weight.semibold, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>{count}</span>
        )}
      </button>
      {summary}
    </div>
    {open && (
      <div id="cf-filter-panel" style={{
        display: 'flex', flexDirection: 'column', gap: space.sm, marginTop: space.sm,
        padding: space.md, border: `1px solid ${B.divider}`, borderRadius: radius.md, background: B.surface,
      }}>
        {children}
      </div>
    )}
  </div>
);

// ---- include / exclude by checkbox ------------------------------------------
//
// CHECKBOXES, NOT TOGGLE PILLS. An "on" pill and an "off" pill were told apart
// by a fill and a strike-through — a convention a reader has to learn, and one
// that does not read down a LIST. A ticked box is a convention nobody has to
// learn, and the owner entities are a list of them, one per Sage entity that
// owns a deal in the run.
//
// STATE IS WHAT IS EXCLUDED, not what is included, so a hold type or an entity
// that appears in the book tomorrow is in the run by default rather than
// silently left out by a saved "included" list that never heard of it. The
// request sends the INCLUDED list (the API's contract), derived at the point of
// sending.
//
// THE LAST ONE CANNOT BE UNTICKED. Zero capital types is an empty report with
// nothing on it to say why; the last box stays ticked, disabled, and says so in
// its title. `allowNone` lifts that rule for a group that is not the run's
// population; the owner entities ARE a partition of the deals (every deal is
// under at least one owner or under "No entity"), so the last owner stays
// ticked like the last capital type. A long list gets All / None, since a
// roster of forty entities is not something to untick one by one.
//
// `columns` lays a long list out in a scrolling grid rather than one wrapping
// line, which is how a list of entities stays readable at forty rows.
const NO_HOLD_TYPE = 'none';

// THE OWNER PICKER'S "no entity" row — the deals none of whose tagged
// locations roll up to any Sage entity (or with no Sage tagging at all). The
// same sentinel the API takes (ownerEntityFilter.js NO_OWNER) and the same
// idea as NO_HOLD_TYPE: a filter must not be able to drop a deal for a reason
// nothing on screen states, so every deal is under a box somebody can see.
const NO_OWNER = 'none';
const OWNER_NONE_LABEL = 'No Sage entity';
const HOLD_TYPE_NONE_LABEL = 'No hold type recorded';

const CheckGroup = ({ label, hint, options, excluded, onToggle, onSetAll, ariaLabel, colorFor, allowNone = false, columns = false }) => {
  const included = options.filter((o) => !excluded.has(o.value));
  return (
    <PanelRow label={label} hint={hint}>
      <div role="group" aria-label={ariaLabel} style={columns
        ? {
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '2px 16px',
            flex: 1, minWidth: 0, maxHeight: 220, overflowY: 'auto', paddingRight: 4,
          }
        : { display: 'flex', flexWrap: 'wrap', gap: '4px 18px', flex: 1, minWidth: 0 }}>
        {options.map((o) => {
          const on = !excluded.has(o.value);
          const last = !allowNone && on && included.length === 1;
          return (
            <CheckRow key={o.value} option={o} on={on} last={last}
              hue={colorFor ? colorFor(o.value) : B.navy} onToggle={onToggle} />
          );
        })}
      </div>
      {allowNone && onSetAll && options.length > 1 && (
        <AllNone included={included.length} total={options.length} onSetAll={onSetAll} />
      )}
    </PanelRow>
  );
};

// ONE ROW RENDERER for both the inline group and the dropdown, so a box reads
// the same wherever it sits: a real checkbox, a swatch in the group's hue, the
// label struck through when it is out, and — for a row that is on the list to
// EXPLAIN an absence rather than to be ticked — a disabled box with the reason
// as its hint and title. A disabled row is never `last`: it is not in the run.
const CheckRow = ({ option: o, on, last, hue, onToggle, disabled = false }) => (
  <label
    title={disabled
      ? (o.reason || o.hint || o.label)
      : (last
        ? `${o.label} is the only one left in — the report needs at least one.`
        : (on ? `Untick to leave ${o.label} out of the run` : `Tick to put ${o.label} back into the run`))}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: font.sm, lineHeight: '22px',
      color: disabled ? B.textLt : (on ? B.text : B.textLt),
      cursor: (last || disabled) ? 'default' : 'pointer', minWidth: 0, opacity: disabled ? 0.75 : 1,
    }}
  >
    <input
      type="checkbox"
      checked={disabled ? false : on}
      disabled={last || disabled}
      onChange={() => { if (!disabled) onToggle(o.value); }}
      style={{ margin: 0, accentColor: hue, cursor: (last || disabled) ? 'default' : 'pointer', flex: 'none' }}
    />
    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: hue, opacity: (on && !disabled) ? 1 : 0.3, flex: 'none' }} />
    <span style={{ textDecoration: (on || disabled) ? 'none' : 'line-through', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
    {o.hint && <span style={{ color: B.textLt, fontSize: font.xs, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.hint}</span>}
  </label>
);

const AllNone = ({ included, total, onSetAll }) => (
  <span style={{ display: 'inline-flex', gap: 6, fontSize: font.xs, color: B.textLt, lineHeight: '22px', flex: 'none' }}>
    <button type="button" onClick={() => onSetAll(true)} disabled={included === total}
      style={{ ...linkBtn, opacity: included === total ? 0.5 : 1 }}>All</button>
    ·
    <button type="button" onClick={() => onSetAll(false)} disabled={included === 0}
      style={{ ...linkBtn, opacity: included === 0 ? 0.5 : 1 }}>None</button>
  </span>
);

// ---- a dropdown of checkboxes -------------------------------------------------
//
// A LIST THAT CAN GROW GETS A DROPDOWN, NOT A ROW. The four capital types are a
// fixed vocabulary and fit on one line; the Sage entities and the R/E hold
// types are read off the server and are as long as the book makes them — a
// dozen holding companies laid out inline wrapped to three rows and read as
// clutter, which is what the reader said. The closed control carries the one
// fact a reader needs at a glance ("7 of 9 entities"), and the panel behind it
// holds the boxes, a filter box once the list is long enough to need one, and
// All / None where the group allows none.
//
// It is a real button with aria-haspopup / aria-expanded, closes on Escape and
// on a click outside, and the panel is absolutely positioned under it — inside
// the filter panel, which is not a scroller, so nothing clips it.
//
// `extras` are rows that EXPLAIN an absence: things on the list that cannot be
// ticked, each with the reason — a roster entity no deal has attached, say.
// They render disabled under their own heading, never mixed into the ticks,
// and never counted in the summary — the summary is about the run.
const CheckDropdown = ({ label, hint, noun, options, excluded, onToggle, onSetAll, ariaLabel, colorFor, allowNone = false, extras = [], extrasHeading }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const included = options.filter((o) => !excluded.has(o.value));
  const total = options.length;
  const summary = total === 0
    ? `None attached in this run`
    : (included.length === total
      ? `All ${total} ${noun}`
      : (included.length === 0 ? `None of ${total} ${noun}` : `${included.length} of ${total} ${noun}`));
  const needle = q.trim().toLowerCase();
  const matches = (o) => !needle || `${o.label} ${o.hint || ''}`.toLowerCase().includes(needle);
  const shown = options.filter(matches);
  const shownExtras = extras.filter(matches);
  const narrowed = included.length !== total;
  return (
    <PanelRow label={label} hint={hint}>
      <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px',
            border: `1px solid ${narrowed ? B.navy : B.border}`, borderRadius: radius.md,
            background: B.surface, color: narrowed ? B.navyDk : B.text, cursor: 'pointer',
            fontFamily: 'inherit', fontSize: font.sm, fontWeight: narrowed ? weight.semibold : weight.medium,
          }}
        >
          {summary}
          <span aria-hidden="true" style={{ fontSize: font.xs, color: B.textLt }}>▾</span>
        </button>
        {open && (
          <div role="group" aria-label={ariaLabel} style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 40, marginTop: 4,
            minWidth: 360, maxWidth: 520, padding: space.sm,
            background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.md, boxShadow: B.shadowLg,
          }}>
            <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', marginBottom: 6 }}>
              {(options.length + extras.length) > 8 && (
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Filter…"
                  aria-label={`Filter ${noun}`}
                  autoFocus
                  style={{ flex: 1, padding: '4px 8px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.sm, fontFamily: fonts.sans, outline: 'none' }}
                />
              )}
              {allowNone && onSetAll && total > 1 && (
                <AllNone included={included.length} total={total} onSetAll={onSetAll} />
              )}
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {shown.map((o) => {
                const on = !excluded.has(o.value);
                const last = !allowNone && on && included.length === 1;
                return (
                  <CheckRow key={o.value} option={o} on={on} last={last}
                    hue={colorFor ? colorFor(o.value) : B.navy} onToggle={onToggle} />
                );
              })}
              {shown.length === 0 && shownExtras.length === 0 && (
                <span style={{ fontSize: font.xs, color: B.textLt, padding: '4px 0' }}>Nothing matches.</span>
              )}
              {shownExtras.length > 0 && (
                <>
                  <div style={{
                    marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${B.divider}`,
                    fontSize: font.xs, fontWeight: weight.semibold, color: B.textLt, textTransform: 'uppercase', letterSpacing: 0.4,
                  }}>{extrasHeading || 'Not in this run'}</div>
                  {shownExtras.map((o) => (
                    <CheckRow key={`x-${o.value}`} option={o} on={false} last={false}
                      hue={colorFor ? colorFor(o.value) : B.navy} onToggle={() => {}} disabled />
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PanelRow>
  );
};

// ---- the capital-type bubble ----------------------------------------------
//
// THE ONE CHIP LEFT ON A ROW, and it is the one a reader needs at a glance:
// what KIND of capital this is. The rest — which model projected it, what the
// mark is, what debt came off the sale, whether a note has matured — used to be
// four more chips apiece, each carrying its meaning in a `title` nobody hovers
// by accident. All of it now lives in the method card, where it can be laid out
// and read; a row of five bubbles was a summary of a summary.
//
// The colours are the deal page's own (constants/detailTypes.js), so a capital
// item is the same colour wherever it appears. A second copy of that map here
// is how the two screens would come to badge one item two different colours.
const TypeBadges = ({ types }) => {
  const list = (types || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <>
      {list.map((t) => (
        <span key={t} title={labelOf(t)}>
          <Badge color={colorOf(t)}>{shortOf(t)}</Badge>
        </span>
      ))}
    </>
  );
};

// ---- the hover card -------------------------------------------------------
//
// POSITION: FIXED, AND THAT IS NOT A STYLE CHOICE. The matrix lives inside an
// `overflow-x: auto` scroller so a hundred period columns can scroll under a
// sticky first column — and an absolutely-positioned panel inside that scroller
// is clipped by it, which would render the card as a sliver at the row's edge.
// A fixed panel is positioned against the viewport and no ancestor clips it, so
// the offsets are MEASURED off the trigger's own bounding rect rather than
// derived from the layout.
//
// AND IT IS PORTALED TO `document.body`, BECAUSE FIXED IS NOT ENOUGH ON ITS
// OWN. The trigger sits inside the sticky first-column cell, and a
// `position: sticky` cell carrying a `z-index` is a STACKING CONTEXT — so a
// fixed child's `zIndex: 60` competed only with that cell's other children,
// while the sticky cells of every LATER row (same z-index, later in the DOM)
// painted straight over it. The card rendered with the deal names of the next
// five rows drawn across its left half and its right half intact over the
// ordinary number cells, which have no stacking context of their own. Rendering
// the panel under `document.body` takes it out of the cell's context entirely,
// so its z-index is measured against the page, which is what it was written
// against. The measured offsets are viewport-relative already, so nothing
// about the positioning changes.
//
// IT IS REACHABLE THREE WAYS, and each one is load-bearing. HOVER, because
// that is what the row asks for. FOCUS, because the card is now the only place
// a figure's provenance lives and it replaced chips that were at least readable
// as text — so the ⓘ is a real button carrying the label, which is also the
// only handle a capital item's row has (its name is a plain span, not a link).
// CLICK, because a touch device has no hover at all and would otherwise reach
// none of this.
//
// THE CARD IS HOVERABLE, WHICH IS WHY CLOSING IS DELAYED. It is scrollable —
// a multi-item deal's card runs past its own height — and it sits 6px below the
// trigger, so crossing that gap leaves the trigger and would close the card
// before the pointer arrived. A short close timer, cancelled when the card
// itself is entered, is what makes the panel usable rather than a flash.
//
// IT FLIPS RATHER THAN OVERFLOWING. Near the bottom of the window it opens
// upward, and it is clamped to the viewport horizontally: a card that opens off
// screen is worse than the chips it replaced.
const CARD_W = 380;
const CARD_MAX_H = 460;
const CLOSE_DELAY_MS = 140;

function HoverInfo({ children, card, label }) {
  const [at, setAt] = useState(null);
  const ref = useRef(null);
  const timer = useRef(null);

  const cancelClose = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  };
  const open = () => {
    cancelClose();
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    setAt({
      left: Math.max(8, Math.min(r.left, window.innerWidth - CARD_W - 8)),
      // Flip upward when there is not room beneath, so the card is never cut
      // off by the bottom of the window.
      ...(below < 240 && r.top > below
        ? { bottom: window.innerHeight - r.top + 6 }
        : { top: r.bottom + 6 }),
    });
  };
  const close = () => { cancelClose(); setAt(null); };
  const closeSoon = () => {
    cancelClose();
    timer.current = setTimeout(() => setAt(null), CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelClose(), []);

  useEffect(() => {
    if (!at) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    // A scroll moves the trigger out from under a fixed panel, so the card
    // closes rather than hanging where the row no longer is.
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
    };
  }, [at]);

  return (
    <span
      ref={ref}
      onMouseEnter={open}
      onMouseLeave={closeSoon}
      // React's focus events bubble, so tabbing to the deal name inside opens
      // the card too — without the wrapper becoming a second tab stop of its
      // own in front of it.
      onFocus={open}
      onBlur={closeSoon}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      {children}
      <button
        type="button"
        aria-label={label}
        aria-expanded={!!at}
        onClick={() => (at ? close() : open())}
        style={{
          background: 'none', border: 'none', padding: 0, margin: 0, display: 'flex',
          color: B.textLt, cursor: 'help', flexShrink: 0, lineHeight: 0,
        }}
      >
        <Icon name="info" size={12} />
      </button>
      {at && createPortal(
        <div
          role="tooltip"
          onMouseEnter={cancelClose}
          onMouseLeave={closeSoon}
          style={{
            position: 'fixed', ...at, width: CARD_W, maxHeight: CARD_MAX_H, overflowY: 'auto',
            zIndex: 60, background: B.surface, border: `1px solid ${B.border}`,
            borderRadius: radius.xl2, boxShadow: B.shadowLg,
            padding: '12px 14px', fontSize: font.sm, color: B.text, lineHeight: 1.5,
            textAlign: 'left', fontWeight: weight.regular, cursor: 'default',
          }}
        >{card}</div>,
        document.body,
      )}
    </span>
  );
}

// One component of a method: its label and its amount, with the annual ones
// marked as annual.
//
// AN ANNUAL FIGURE PRINTED BESIDE A LIFETIME ONE WITH NO UNIT reads as a
// lifetime total. A stabilized NOI of $440k beside a capital cost of $5.3M
// invites the reader to conclude the asset never pays for itself; over a
// four-year hold it is $1.76M. The API marks which components are per-year
// (`per`) precisely so this label is not a guess.
const MethodLine = ({ c }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '2px 0' }}>
    <span style={{ color: B.textMd }}>
      {c.label}
      {c.per === 'year' && <span style={{ color: B.textLt, fontSize: font.xs }}> /yr</span>}
    </span>
    <span style={{
      fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      color: c.amount < 0 ? B.red : B.text,
    }}>{fmt.currency(c.amount)}</span>
  </div>
);

const CardHead = ({ children }) => (
  <div style={{
    fontSize: font.xs, textTransform: 'uppercase', letterSpacing: 0.5,
    color: B.textMd, fontWeight: weight.semibold, marginBottom: 4,
  }}>{children}</div>
);

// ONE CAPITAL ITEM'S METHOD, in components — the card's unit.
//
// EVERY FIGURE HERE CAME FROM THE API's `basis` BLOCK. The page composes the
// layout and formats the numbers; which components a source has, and the words
// for them, are the engine's (cashFlowIrr.js `basisOf`) so the two clients
// cannot own different answers to "how was this arrived at".
function MethodSection({ basis, name, showName }) {
  if (!basis) return null;
  const { components = [], valuation, notes = [], ledger, label, reason } = basis;
  return (
    <div style={{ marginBottom: 10 }}>
      {showName && (
        <div style={{ fontWeight: weight.semibold, color: B.navyDk, marginBottom: 2 }}>
          {name}
          {basis.detailType && (
            <span style={{ marginLeft: 6 }}><Badge color={colorOf(basis.detailType)}>{shortOf(basis.detailType)}</Badge></span>
          )}
        </div>
      )}
      {label ? (
        <div style={{ color: B.textMd, marginBottom: 6 }}>Projected from <strong style={{ color: B.text }}>{label}</strong>.</div>
      ) : (
        <div style={{ color: B.amber, marginBottom: 6 }}>
          {/* Nothing projects this forward, and the reason is the actionable
              half — "no budget, capital schedule or valuation" tells a reader
              which of three things to go and record. */}
          {reason || 'Nothing projects this capital item forward.'}
        </div>
      )}

      {components.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {components.map((c) => <MethodLine key={c.key} c={c} />)}
        </div>
      )}

      {valuation && (
        <div style={{
          background: B.surfaceMuted, border: `1px solid ${B.divider}`,
          borderRadius: radius.md, padding: '7px 9px', marginBottom: 8,
        }}>
          <CardHead>Valuation</CardHead>
          {/* THE CARRYING VALUE AND THE EXIT VALUE ARE DIFFERENT CLAIMS and are
              printed apart: the first is what the item is worth now (and what
              the unrealized rate sells at), the second what the mark path says
              it is worth on the sale date. One figure labelled "value" is how
              next decade's forecast comes to be read as today's number. */}
          {valuation.markValue != null && (
            <MethodLine c={{ key: 'mark', label: `Carried at${valuation.markAsOfDate ? ` (${fmt.date(valuation.markAsOfDate)})` : ''}`, amount: valuation.markValue }} />
          )}
          {valuation.exitValue != null && valuation.exitValue !== valuation.markValue && (
            <MethodLine c={{ key: 'atExit', label: 'Value at exit', amount: valuation.exitValue }} />
          )}
          {valuation.debtOutstanding ? (
            <MethodLine c={{ key: 'debt', label: 'Debt outstanding', amount: -Math.abs(valuation.debtOutstanding) }} />
          ) : null}
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 3 }}>
            {valuation.method && <>Method: {valuation.method}. </>}
            {valuation.exitDate && <>Sale dated {fmt.date(valuation.exitDate)}{valuation.exitBasis === 'planned' ? ' (planned)' : valuation.exitBasis === 'timeToSale' ? ' (as-of + time to sale)' : ''}. </>}
            {!valuation.used && <>This mark is not this row’s exit — the model above supplies one. </>}
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <ul style={{ margin: '0 0 6px', paddingLeft: 16, color: B.textMd, fontSize: font.xs }}>
          {notes.map((n, i) => <li key={i} style={{ marginBottom: 2 }}>{n}</li>)}
        </ul>
      )}

      {ledger && (
        // THE ONE LINE ABOUT SAGE, AND DELIBERATELY NOT A LIST OF POSTINGS. This
        // card explains the valuation METHOD; a capital item's ledger runs to
        // hundreds of rows and enumerating them would bury the five figures that
        // answer the question. It is mentioned at all because the row's early
        // columns are realized cash and would otherwise be unexplained.
        <div style={{
          borderTop: `1px dashed ${B.divider}`, paddingTop: 6,
          fontSize: font.xs, color: B.textLt,
        }}>
          <strong style={{ color: B.textMd }}>{fmt.currency(ledger.net)}</strong> net realized across{' '}
          {ledger.postingDays} posting day{ledger.postingDays !== 1 ? 's' : ''}
          {ledger.firstDate ? `, ${fmt.date(ledger.firstDate)} to ${fmt.date(ledger.lastDate)}` : ''}.{' '}
          {ledger.note}
        </div>
      )}
    </div>
  );
}

// A DEAL'S CARD IS ITS CAPITAL ITEMS' CARDS, plus the one thing that belongs to
// the deal and to no item: debt booked on a capital item that projects nothing,
// repaid out of a sibling's sale. That used to be a chip on the row, and the
// row simply reads low against a mark the reader can see on the deal unless the
// subtraction is stated.
function DealMethodCard({ deal }) {
  const items = deal.details || [];
  const debt = deal.debt || null;
  return (
    <div>
      <div style={{ fontWeight: weight.semibold, color: B.navyDk, marginBottom: 2 }}>{deal.name}</div>
      <div style={{ fontSize: font.xs, color: B.textLt, marginBottom: 8 }}>
        How this row’s money was arrived at — one section per capital item.
      </div>
      {items.map((item) => (
        <MethodSection key={item.dealDetailID} basis={item.basis} name={item.label} showName={items.length > 1} />
      ))}
      {debt && debt.allocated > 0 && (
        <div style={{ borderTop: `1px solid ${B.divider}`, paddingTop: 6, fontSize: font.xs, color: B.textMd }}>
          <strong>{fmt.currency(debt.allocated)}</strong> of debt sits on a capital item of this deal that
          projects no cash flow of its own, so it is repaid out of this deal’s sale. The exit is the mark
          less that amount.
        </div>
      )}
      {debt && debt.unallocated > 0 && (
        <div style={{ borderTop: `1px solid ${B.divider}`, paddingTop: 6, fontSize: font.xs, color: B.amber }}>
          {debt.reason || `${fmt.currency(debt.unallocated)} of debt could not be placed.`}
        </div>
      )}
      {!items.length && <div style={{ color: B.textMd }}>This deal has no capital items.</div>}
    </div>
  );
}

// ---- notes, marked at the row and collected at the foot -------------------
//
// EVERY LOOSE ASIDE ON A ROW — why a deal isn't projected, a Sage entity's
// note, the caveat behind a rate — used to be its own italic fragment or an
// inline ⚠, each with its own native `title` tooltip: a different interaction
// per note, none of which work on a touchscreen, and none of which survive a
// print. A row or figure with one now carries a single `*`; the sentence
// itself lives once, collected at the foot of the report (see
// `collectRowNotes` in cashFlowFormat.js and ReportNotesDisclosure below).
// Clicking the mark opens that section and scrolls it into view, so "the
// event" and "the note" stay one click apart rather than a hover-and-guess.
const NoteMark = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="See notes about this report, collected below the table"
    style={{
      background: 'none', border: 'none', padding: 0, margin: '0 0 0 2px',
      color: B.textLt, cursor: onClick ? 'pointer' : 'default', fontFamily: 'inherit',
      fontSize: '0.9em', lineHeight: 1, verticalAlign: 'text-top',
    }}
  >*</button>
);

// ---- the rate cells -------------------------------------------------------
//
// A DASH IS NOT A ZERO, and the reason is what makes the dash readable. Every
// null rate the API sends carries a sentence naming which of three things
// happened — no valuation on file, no Sage tagging, or flows that never change
// direction — and each names a different fix on a different screen. Printing
// "0.0%" instead would report a position as breaking even.
const RateCell = ({ block, strong = false, onNoteClick }) => {
  if (!block) return <span style={{ color: B.textLt }}>—</span>;
  const text = formatRate(block.irr);
  if (!text) {
    return (
      <span title={block.reason || 'This rate cannot be solved from what is recorded.'}
        style={{ color: B.textLt, cursor: 'help' }}>—</span>
    );
  }
  const sign = rateSign(block.irr);
  return (
    <span
      title={rateTitle(block)}
      style={{
        color: sign === 'down' ? B.red : sign === 'up' ? B.green : B.text,
        fontWeight: strong ? weight.semibold : weight.regular,
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', cursor: 'help',
      }}
    >
      {text}
      {/* A DEAL RATE THAT CREDITS NONE OF WHAT AN UNVALUED SIBLING HOLDS IS
          REAL AND INCOMPLETE. The API says so in words; the mark points at
          the sentence, collected below, instead of repeating it as a
          floating ⚠. */}
      {block.caveat && <NoteMark onClick={onNoteClick} />}
    </span>
  );
};

// What the rate assumed, in a sentence — the working, not just the result.
function rateTitle(block) {
  const bits = [];
  if (block.basis === 'unrealized') {
    bits.push('Realized Sage cash, then a sale today at what the position is carried at today.');
    if (block.grossValue != null) {
      bits.push(`Sold at ${fmt.currency(block.grossValue)}${block.debtOutstanding ? ` less ${fmt.currency(block.debtOutstanding)} of debt` : ''}${block.valueDate ? ` on ${fmt.date(block.valueDate)}` : ''}.`);
    }
    if (block.terminalValue != null && block.debtOutstanding) bits.push(`Net proceeds ${fmt.currency(block.terminalValue)}.`);
  } else {
    bits.push('Realized Sage cash, then every remaining flow this report projects — the exit included.');
    if (block.exitDate) bits.push(`Exit dated ${fmt.date(block.exitDate)}${block.exitValue != null ? ` at ${fmt.currency(block.exitValue)}` : ''}.`);
  }
  if (block.flowCount) bits.push(`${block.flowCount} cash flow${block.flowCount !== 1 ? 's' : ''}${block.firstDate ? ` from ${fmt.date(block.firstDate)}` : ''}.`);
  if (block.caveat) bits.push(block.caveat);
  return bits.join(' ');
}

// `err` exists because a FAILED export must not render as "done, with a
// caveat" — amber is the tone for a number that needs reading carefully, not
// for work that did not happen.
const CAVEAT_TONES = {
  info: { accent: null, icon: 'info' },
  warn: { accent: 'amber', icon: 'warning' },
  err:  { accent: 'red',   icon: 'warning' },
};
const Caveat = ({ tone = 'info', children }) => {
  const { accent, icon } = CAVEAT_TONES[tone] || CAVEAT_TONES.info;
  const hue = accent ? B[accent] : null;
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start',
      background: hue ? `${hue}12` : B.surfaceMuted,
      border: `1px solid ${hue ? `${hue}40` : B.divider}`,
      borderRadius: radius.lg, padding: '9px 12px', marginBottom: space.md,
      fontSize: font.sm, color: B.textMd, lineHeight: 1.5,
    }}>
      <span style={{ color: hue || B.textLt, flexShrink: 0, marginTop: 1 }}>
        <Icon name={icon} size={14} />
      </span>
      <div>{children}</div>
    </div>
  );
};

// THE REPORT'S CAVEATS, COLLECTED AND COLLAPSED AT THE FOOT OF THE PAGE. Each
// one still qualifies a number above it — a narrowing filter, an incomplete
// rate, a truncated axis — but stacked open between the KPI strip and the
// table they crowded out the report itself before a reader ever reached a
// number. CLOSED BY DEFAULT, same as FilterDisclosure: the header names how
// many notes there are and, in its color, whether any need real attention, so
// a closed panel never hides THAT something is off — it only hides the prose
// until the reader asks for it.
const ReportNotesDisclosure = ({ items, open, onToggle }) => {
  if (!items.length) return null;
  const urgent = items.some((it) => it.tone === 'warn' || it.tone === 'err');
  const hue = urgent ? B.amber : B.navy;
  return (
    <div style={{ marginTop: space.lg, borderTop: `1px dashed ${B.divider}`, paddingTop: space.sm }}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="cf-notes-panel"
        onClick={onToggle}
        title={open ? 'Hide these notes' : 'Show notes about this report — narrowing filters, incomplete data, and other caveats'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px 5px 8px',
          border: `1px solid ${open ? hue : B.border}`, borderRadius: radius.md,
          background: B.surface, color: urgent ? hue : B.navyDk, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: font.sm, fontWeight: weight.semibold,
        }}
      >
        <span aria-hidden="true" style={{
          display: 'inline-block', fontSize: font.xs, transition: 'transform .15s',
          transform: open ? 'rotate(90deg)' : 'none',
        }}>▶</span>
        <Icon name={urgent ? 'warning' : 'info'} size={13} />
        Notes about this report
        <span aria-label={`${items.length} note${items.length !== 1 ? 's' : ''}`} style={{
          minWidth: 18, height: 18, padding: '0 5px', borderRadius: radius.pill, background: hue, color: '#fff',
          fontSize: 11, fontWeight: weight.semibold, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{items.length}</span>
      </button>
      {open && (
        <div id="cf-notes-panel" style={{ marginTop: space.sm }}>
          {items.map((it) => <Caveat key={it.key} tone={it.tone}>{it.body}</Caveat>)}
        </div>
      )}
    </div>
  );
};

// A single KPI figure in the summary strip.
const Kpi = ({ label, value, hint, tone }) => (
  <div style={{ minWidth: 130 }}>
    <div style={{ fontSize: font.xs, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: weight.semibold }}>{label}</div>
    <div style={{ fontSize: font.xl, fontWeight: weight.semibold, color: tone || B.text, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {hint && <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 2 }}>{hint}</div>}
  </div>
);

// ---- the growth chart -----------------------------------------------------
//
// WHERE A DEPLOYED DOLLAR ENDED UP, ours against the same dollar put into the
// index ON THE SAME DATE, cumulated over every deal. That is what these curves
// are, and it replaces a comparison that could not have been right:
//
//   * THE MARKET'S SPAN USED TO BE PICKED FROM A CONTROL. A "3yr" segmented
//     button set the market window, and the deals' x axis was elapsed years
//     with no reference to when any capital actually went out. A book that
//     deployed eighteen months ago was read against the S&P's last three years
//     because that was where the control happened to sit. The window is now
//     DERIVED — the money-weighted holding period of our own realized capital —
//     so the control is gone, and there is nothing left to set wrongly.
//   * OUR SIDE USED TO AVERAGE RATES, NOT DOLLARS. `report.weighted` is the
//     mean of each deal's IRR weighted by its capital, and a capital weight
//     suppresses a deal's SIZE while leaving its RATE loose in the mean: a
//     capital item with $40 out against a real inflow solves to four figures a
//     year, and the shipped chart printed +845,488% for the unrealized curve
//     over three years. It rendered perfectly. The curves now come from
//     `report.marketComparison`, whose deal side is the PORTFOLIO rate —
//     `report.irr`, one solve over the union of every flow in the book, which
//     the table's "All deals" row has always printed. A $40 outlier now
//     contributes $40 of weight and nothing else.
//
// SO THE CHART AND THE TABLE'S "ALL DEALS" ROW NOW AGREE, deliberately. Two
// figures for one book, differing by which measure each happened to use, is
// what sent somebody to read this code in the first place.
//
// THE ENDPOINTS ARE THE FIGURES; THE PATHS ARE ONE NUMBER DRAWN OUT. Every
// curve is `(1 + r)^t - 1`, which is exactly what a rate asserts and is not a
// forecast of a path — so the market's is no longer "measured" in the way it
// was, and the card says so. What IS measured is where each curve ENDS: the
// market's endpoint is the real matched multiple, every dollar of realized
// capital priced into the index on its own date and valued at the as-of date.
// The multiples are printed beside the chart for that reason.
//
// A CURVE STOPS AT ITS OWN HORIZON rather than running to the full axis. The
// market alternative is valued at the as-of date and has nothing to say about
// the year after it, so its line and our unrealized line end there and the
// projected line carries on alone — which is the honest picture, since no
// forward market return exists to price a modelled 2030 exit against.
//
// THE THREE QUALIFICATIONS, ALL ON THE CARD:
//   * COVERAGE. The index carries ten years, so a deal that deployed capital
//     before its history begins cannot be priced and is dropped WHOLE — half a
//     deal's cost against all of its value would read as beating the market.
//     The card names the capital left out, in dollars.
//   * IT IS A PRICE INDEX. FRED's SP500 series excludes dividends, so it sits
//     roughly 1.5-2 points below a total-return benchmark — a knowable bias in
//     favour of the deals, stated rather than silently corrected by a number
//     nobody sourced.
//   * OUR UNREALIZED SIDE RESTS ON MARKS. A deal carrying realized capital and
//     no current valuation has no book value to compare, so it is dropped too
//     and counted apart: that one is a missing mark somebody can go and record,
//     which is a different fix from a licensing limit.
//
// OUR TWO CURVES SHARE ONE HUE AND ARE TOLD APART BY DASH, with the spread
// between them filled. They are the same book under two assumptions, so a
// second hue would read as a third party; the band is the thing worth seeing —
// how much of the projected return is still an assumption about the exit. The
// palette is two hues rather than three for a harder reason too: it was checked
// with the validator rather than by eye, and the brand's remaining categorical
// slots either sit outside the lightness band or collide with the green/red
// this same table already uses to tone a rate's sign.
const SERIES_MARKET = B.amber;      // #c47d1a
// The brand accent (#2b7a8c) fails the validator's chroma floor as a series
// slot, so this is its nearest passing step. Do not swap either of these for a
// token by eye — re-run scripts/validate_palette.js on the pair.
const SERIES_HARVUS = '#00829e';
const PLOT_H = 260;
const PLOT_PAD = { top: 14, right: 96, bottom: 26, left: 56 };
const FALLBACK_W = 760;

// Every curve is drawn from a rate, so all of them are smooth by construction —
// that IS what a rate asserts. WHICH rate is the server's decision, not this
// file's: a measured line carries the rate implied by its multiple so it lands
// on it, and the projected line carries its solved rate because it has no
// multiple. See `curveAnchorFor` in api/src/cashFlowPme.js.
function pathOf(segments, x, y) {
  return segments
    .map((run) => run.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(2)},${y(p.v).toFixed(2)}`).join(''))
    .join(' ');
}

/**
 * THE ONE CONTROL ON THIS PAGE THAT CHANGES A FIGURE RATHER THAN FILTERING ONE.
 *
 * Past the index's last observation there is nothing to measure, so the market
 * line is carried forward at an assumed rate. `cashFlowPme.js`'s header used to
 * say flatly that no forward market return exists and that inventing one is not
 * honest — right about a DERIVED figure, and wrong as a refusal: a projected
 * return running to a 2050 exit has no comparator at all without one. The
 * honest form is an assumption the reader can SEE and CHANGE, drawn dashed,
 * with the spread between the lines as the claim rather than the level of
 * either.
 *
 * THE TRAILING FIGURE IS PRINTED BESIDE IT AND IS DELIBERATELY NOT THE DEFAULT.
 * It is honest context — what the index actually did — and adopting it as a
 * forecast assumes the best decade in a generation repeats. Showing it is what
 * makes the choice informed; defaulting to it would make it for the reader.
 *
 * AND THE FIRM CAN HOLD A VIEW OF ITS OWN, which is what this control's unset
 * option means. Before that row the assumption lived only in this component's
 * state, so a PME ratio quoted off an 8% view was irreproducible by the next
 * person to open the page — who got the engine's constant, with nothing on
 * either screen saying why the two disagreed. An Admin sets it HERE rather than
 * on a Settings tab, because the decision is worth making in front of the two
 * lines it moves; the API is the real gate, this only shows the affordance.
 * Everything the label says comes from the SERVER (`report.forwardGrowth`) —
 * the rate in force, the firm's row, and the engine's default — so no figure
 * on this side can go stale against the one that drew the chart.
 */
function ForwardGrowthControl({ value, onChange, report, user, onSaved }) {
  const bm = (report && report.benchmark) || null;
  const windows = (bm && Array.isArray(bm.windows) ? bm.windows : []);
  // The LONGEST window available — the most information, and the least
  // flattering to a short-run market move (benchmarkWindows' own rule).
  const trailing = windows.length ? windows[windows.length - 1] : null;

  // WHAT IS ACTUALLY IN FORCE, AND WHERE IT CAME FROM — from the server, never
  // from this control's own value. An out-of-range or unreadable choice is
  // normalized server-side, so a control reporting its own input would disagree
  // with the line it drew. `defaultPct` travels too, so the engine's long-run
  // figure is never restated here: a second copy of 6.5% on the client is the
  // one that goes stale when the engine's changes.
  const fg = (report && report.forwardGrowth) || null;
  const isAdmin = (user && user.Role) === 'Admin';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // The firm's row, as a percent string, or '' when nobody has taken a view.
  const firmPct = fg && fg.settingPct != null ? String(fg.settingPct) : '';
  // "Set as firm default" is offered only for a choice that would CHANGE the
  // stored view. Offering it over the figure already stored is a button that
  // appears to do something and does nothing.
  const chosenPct = value === '' ? null : value;
  const canSave = isAdmin && chosenPct !== null && String(chosenPct) !== firmPct;
  const canClear = isAdmin && firmPct !== '';

  // One write, through the settings endpoint every other system-wide value uses
  // — a second endpoint for one key is a second copy of the Admin gate. CLEAR
  // WRITES AN EMPTY VALUE rather than the current figure: no view is a distinct
  // state from a view that happens to match the platform's, and only the first
  // keeps tracking the constant as it changes.
  const write = async (pct) => {
    setBusy(true); setErr(null);
    try {
      await api.put('settings', { cashFlowForwardGrowthPct: pct });
      onSaved && onSaved();
    } catch (e) { setErr(e.message || 'Could not save'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: font.sm, color: B.textMd }}>
      <label htmlFor="cf-growth">Market after today</label>
      {/* A plain select styled like the date inputs beside it, NOT the kit's
          `Select` — that one is a form field carrying its own label and a block
          margin, which would break this row's alignment. */}
      <select
        id="cf-growth" value={value} onChange={(e) => onChange(e.target.value)}
        aria-label="Assumed forward market return"
        style={{
          padding: '6px 9px', border: `1px solid ${B.border}`, borderRadius: radius.md,
          fontSize: font.sm, fontFamily: fonts.sans, background: B.surface, outline: 'none',
          color: B.text, cursor: 'pointer',
        }}
      >
        {FORWARD_GROWTH_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {/* The unset option names what unset MEANS, and which of the two
                things it means — the firm's view, or the platform's because
                nobody has taken one. Both figures come from the server. */}
            {o.value === '' && fg
              ? (firmPct !== '' ? `Firm default (${firmPct}%)` : `Default (${fg.defaultPct}%)`)
              : o.label}
          </option>
        ))}
      </select>
      <span style={{ fontSize: font.xs, color: B.textLt }}>
        {fg && <>{fg.pct}% a year, price-only</>}
        {trailing && trailing.irr != null && (
          <> · trailing {trailing.years}yr was {(trailing.irr * 100).toFixed(1)}%</>
        )}
      </span>

      {/* A HOUSE VIEW THAT IS SET AND DOES NOTHING is the one failure a reader
          cannot otherwise see — the report falls back silently, exactly as it
          does for a bad query param, and nobody is sat at the field when it
          runs. */}
      {fg && fg.settingInvalid && (
        <span style={{ fontSize: font.xs, color: B.amber }}>
          The firm default on file (“{String(fg.settingPct)}”) is not a usable rate, so it is being ignored.
        </span>
      )}

      {/* THE DECISION IS MADE IN FRONT OF THE EVIDENCE, which is why this lives
          on the chart and not on a Settings tab: an Admin picks a rate, watches
          the two lines move, and only then makes it the firm's. A 14th Settings
          tab for one number would also over-weight it (the
          SageExcludedAccountsPanel reasoning). The API is the real gate — this
          is only whether the affordance is shown. */}
      {canSave && (
        <Btn size="sm" variant="secondary" disabled={busy} onClick={() => write(String(chosenPct))}
          title="Everyone who opens this report gets this assumption unless they pick another for their own run">
          {busy ? 'Saving…' : `Set ${chosenPct}% as firm default`}
        </Btn>
      )}
      {canClear && !canSave && (
        <Btn size="sm" variant="ghost" disabled={busy} onClick={() => write('')}
          title="Go back to the platform's long-run figure, and track it as it changes">
          {busy ? 'Clearing…' : 'Clear firm default'}
        </Btn>
      )}
      {err && <span style={{ fontSize: font.xs, color: B.red }}>{err}</span>}
    </div>
  );
}

// ---- the market comparison card -------------------------------------------
//
// THE CARD LEADS WITH A RATE PAIR, NOT A PICTURE. The growth chart's job was to
// show WHEN the money moved and where the assumed half begins, and in practice
// it produced questions about its own axis ("why does the chart start on a
// different date from the table") rather than answers. What people take from
// the card is two rows: our rate against the market's on the same dollars, the
// same dates and the same horizon — sold today at carrying value, and held to
// plan. The curve survives behind a link for anyone who wants the path.
//
// BOTH SIDES OF EACH ROW ARE POOLED SOLVES, computed on the API
// (`report.marketComparison.comparison`) — one rate over the union of the
// flows, never an average of deal rates, so a $40 item contributes $40 and
// nothing else. The client formats and arranges; it solves nothing, derives no
// spread and re-states no assumption.
//
// THE HELD-TO-PLAN MARKET RATE IS PARTLY AN INPUT, and the row says so in the
// engine's words: each capital item builds its own pool of index shares from
// its outflows and sells a weighted slice against every distribution or exit
// still projected, priced on that event's own date — measured to the as-of
// date and then the assumed forward return past it. There is no single
// horizon any more (that collapsed every outflow to one dollar-weighted date,
// which this replaced); a market rate built on an assumption is not a
// measurement unless the reader is told how far it reaches.
//
// THE POPULATION IS THE RUN'S. A deal with realized capital and no valuation
// mark is IN the comparison at cost (a note at its outstanding principal) and
// contributes no gain; the coverage line says how many are carried that way.
// It used to be dropped whole, which put the comparison on 7 deals while the
// table beside it ran over 41 — the disagreement that started this.
function MarketComparisonCard({ report }) {
  const cmp = report.marketComparison || null;
  const benchmark = report.benchmark || null;
  const cov = (cmp && cmp.coverage) || null;
  const rows = (cmp && cmp.comparison) || null;
  const [showCurve, setShowCurve] = useState(false);
  const name = (cmp && cmp.name) || 'the index';

  // Rate points, signed. A null is a dash, never "+0.0 pts" — which would read
  // as level with the market rather than as unanswerable.
  const pts = (v) => (v == null || !Number.isFinite(Number(v)) ? null : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)} pts`);
  const toneOf = (v) => (v == null ? B.textMd : v > 0 ? B.green : v < 0 ? B.red : B.textMd);

  const head = (
    <div style={{ fontSize: font.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, fontWeight: weight.semibold, marginBottom: 6 }}>
      Our deals against {name}
    </div>
  );

  if (!cmp || cmp.available === false || !rows) {
    return (
      <Card style={{ padding: space.lg, marginBottom: space.lg }}>
        {head}
        <div style={{ fontSize: font.sm, color: B.textMd, lineHeight: 1.6 }}>
          {(cmp && cmp.reason) || (benchmark && benchmark.reason)
            || 'No market history is available to price this book’s deployed capital against yet.'}
        </div>
      </Card>
    );
  }

  const u = rows.unrealized || {};
  const pj = rows.projected || {};
  const cell = { padding: '10px 14px', verticalAlign: 'top', borderTop: `1px solid ${B.divider}` };
  const th = { textAlign: 'left', padding: '6px 14px', color: B.textMd, fontWeight: weight.semibold, fontSize: font.xs, textTransform: 'uppercase', letterSpacing: 0.5 };
  const big = { fontSize: font.xl, fontWeight: weight.semibold, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 };
  const small = { fontSize: font.xs, color: B.textLt, marginTop: 3, lineHeight: 1.5 };
  const Rate = ({ v, reason }) => (formatRate(v)
    ? <div style={{ ...big, color: rateSign(v) === 'down' ? B.red : rateSign(v) === 'up' ? B.green : B.text }}>{formatRate(v)}</div>
    : <div style={{ ...big, color: B.textLt }} title={reason || 'Cannot be solved from what is recorded.'}>—</div>);

  return (
    <Card style={{ padding: space.lg, marginBottom: space.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.md, flexWrap: 'wrap', marginBottom: space.sm }}>
        <div>
          {head}
          <div style={{ fontSize: font.xs, color: B.textLt, lineHeight: 1.6 }}>
            {/* WHAT THE COMPARISON IS. Every figure rests on pricing our own
                capital into the index on the dates it actually went out; a
                reader not told that reads the market column as a generic index
                return over some window. */}
            Every dollar of realized capital, priced into <strong>{name} on the date it actually went out</strong>, against
            what the deals themselves are worth — cumulated over{' '}
            {cov ? <>{cov.matchedDeals} deal{cov.matchedDeals !== 1 ? 's' : ''}</> : 'the book'}
            {cmp.deployed != null && <> and {fmt.currency(cmp.deployed)} of capital</>}.
            {' '}<strong>Sold today at carrying value</strong> is always the same &ldquo;Our deals&rdquo; figure the &ldquo;All
            deals&rdquo; row shows in the Table view — the whole book, not only what is matched here.{' '}
            <strong>Held to plan</strong> is <strong>one solve over the union of these matched deals&rsquo; flows</strong> — a
            portfolio rate, not an average of each deal&rsquo;s own rate, so size and timing both count and a small item cannot
            move it
            {cov && cov.share != null && cov.share >= 0.999
              ? <>, and on a fully matched book it is the same figure too.</>
              : <>; the &ldquo;All deals&rdquo; row in the Table view covers the whole book, including any deal left out below, so the two can differ.</>}
          </div>
        </div>
        <Btn variant="ghost" onClick={() => setShowCurve((v) => !v)} aria-pressed={showCurve}>
          {showCurve ? 'Hide the curve' : 'Show the curve'}
        </Btn>
      </div>

      <div style={{ overflowX: 'auto', marginTop: space.sm }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640, fontSize: font.sm }}>
          <thead>
            <tr>
              <th style={th}>Basis</th>
              <th style={{ ...th, color: SERIES_HARVUS }}>Our deals</th>
              <th style={{ ...th, color: SERIES_MARKET }}>{name}</th>
              <th style={th}>Ahead / behind</th>
            </tr>
          </thead>
          <tbody>
            {/* ROW 1 — SOLD TODAY AT CARRYING VALUE. Both sides measured at the
                index's own as-of observation, so the spread is a measurement. */}
            <tr>
              <td style={cell}>
                <div style={{ fontWeight: weight.semibold, color: B.text }}>Sold today at carrying value</div>
                <div style={small}>
                  Realized Sage cash, then every position sold {u.asOf ? `on ${fmt.date(u.asOf)}` : 'today'} at what it is
                  carried at — a mark where one is recorded, a note&rsquo;s outstanding principal, otherwise cost.
                </div>
              </td>
              <td style={cell}>
                <Rate v={u.ours} reason={u.reason} />
                {/* THE DOLLAR ANSWER BESIDE THE RATE, because a rate pair alone
                    cannot say how much money the difference is. */}
                {u.oursMultiple != null && (
                  <div style={small}>{Number(u.oursMultiple).toFixed(2)}× · {fmt.currency(u.oursValue)}</div>
                )}
              </td>
              <td style={cell}>
                <Rate v={u.market} reason={u.reason} />
                {u.marketMultiple != null && (
                  <div style={small}>{Number(u.marketMultiple).toFixed(2)}× · {fmt.currency(u.marketValue)} — the same dollars, on the same dates, in the index</div>
                )}
              </td>
              <td style={cell}>
                <div style={{ ...big, color: toneOf(u.spread) }}>{pts(u.spread) || <span style={{ color: B.textLt }}>—</span>}</div>
                {u.pme != null && (
                  <div style={small}>
                    {u.pme >= 1 ? '+' : '−'}{Math.abs((u.pme - 1) * 100).toFixed(1)}% in dollars vs the index, at today&rsquo;s marks
                  </div>
                )}
              </td>
            </tr>
            {/* ROW 2 — HELD TO PLAN. Our side is the projected rate; the market
                side is the same dollars grown at the observed index to the
                as-of date and at the ASSUMED forward return after it, liquidated
                on the date the plan returns its money. Said on the row. */}
            <tr>
              <td style={cell}>
                <div style={{ fontWeight: weight.semibold, color: B.text }}>Held to plan</div>
                <div style={small}>
                  Realized Sage cash, then every remaining projected flow — the exit included. Each capital item
                  builds its own pool of index shares from its outflows and sells a weighted slice against every
                  distribution or exit still projected, priced on that event&rsquo;s own date
                  {pj.itemCount != null ? <> ({pj.itemCount} item{pj.itemCount !== 1 ? 's' : ''})</> : null}
                  {pj.excludedCount ? <>; {pj.excludedCount} item{pj.excludedCount !== 1 ? 's' : ''} excluded — capital before the index history</> : null}.
                </div>
              </td>
              <td style={cell}>
                <Rate v={pj.ours} reason={pj.reason} />
                <div style={small}>no multiple — the value runs past today and includes calls not yet made</div>
              </td>
              <td style={cell}>
                {/* THE ASSUMPTION IS SAID ONCE, in the caveats below — repeating
                    it per-row read as two notes making the same claim, since
                    that paragraph already names the same growth rate and span. */}
                <Rate v={pj.market} reason={pj.reason} />
              </td>
              <td style={cell}>
                <div style={{ ...big, color: toneOf(pj.spread) }}>{pts(pj.spread) || <span style={{ color: B.textLt }}>—</span>}</div>
                {pj.spread != null && <div style={small}>rests on the assumed market return</div>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: space.md, fontSize: font.xs, color: B.textLt, lineHeight: 1.6 }}>
        {/* THE CAVEATS ARE THE API'S OWN WORDS, verbatim — the client owns no
            copy of why this comparison is an approximation. */}
        {(cmp.caveats || []).join(' ')}
        {cov && cov.share != null && cov.share < 0.999 && (
          <> It covers {formatRate(cov.share)} of the book&rsquo;s realized capital
            ({fmt.currency(cov.deployed)} of {fmt.currency(cov.totalDeployed)}).
            {cov.beforeHistory > 0 && (
              <> {cov.beforeHistory} deal{cov.beforeHistory !== 1 ? 's' : ''}{' '}
                ({fmt.currency(cov.beforeHistoryDeployed)}) moved money before the index history
                begins on {cmp.historyFrom ? fmt.date(cmp.historyFrom) : 'its first observation'}, so
                there is no price to buy at and {cov.beforeHistory !== 1 ? 'they are' : 'it is'} left out
                rather than priced at the earliest level available.</>
            )}
          </>
        )}
      </div>

      {/* THE CURVE, BEHIND A LINK. The path is still the honest picture of WHEN
          the money moved; it is just no longer what the card leads with. */}
      {showCurve && (
        <div style={{ marginTop: space.lg }}>
          <GrowthChart report={report} />
        </div>
      )}
    </Card>
  );
}

function GrowthChart({ report }) {
  const cmp = report.marketComparison || null;
  const benchmark = report.benchmark || null;
  // THE DATED PATH, not the constant-rate curves. `cmp.series` is still shipped
  // (the workbook's Rates sheet explains the solved rates and the multiples
  // they imply) and is deliberately not drawn: every point here is a MEASURED
  // value, so the endpoint agrees with the multiple above it at every date
  // rather than only the last — the rate-vs-multiple mismatch `curveAnchorFor`
  // exists to paper over stops existing on a path.
  const path = (cmp && cmp.path) || null;
  const series = (path && Array.isArray(path.series) ? path.series : []);
  const cov = (cmp && cmp.coverage) || null;
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState(null);

  // MEASURED, NOT SCALED. A `preserveAspectRatio="none"` viewBox is right for a
  // sparkline and wrong here — it would stretch the axis labels and the stroke
  // widths with the container. So the plot is drawn at real pixels and the
  // container's width is read off the DOM.
  const boxRef = useRef(null);
  const [width, setWidth] = useState(FALLBACK_W);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0] && entries[0].contentRect ? entries[0].contentRect.width : 0;
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const axis = useMemo(() => growthAxis(series, ['ours', 'market']), [series]);
  const runs = useMemo(() => ({
    ours: splitAtAssumed(series, 'ours'),
    market: splitAtAssumed(series, 'market'),
  }), [series]);
  const ticks = useMemo(() => yearTicks(series, 10), [series]);

  if (!series.length) {
    return (
      <Card style={{ padding: space.lg, marginBottom: space.lg }}>
        <div style={{ fontSize: font.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, fontWeight: weight.semibold, marginBottom: 6 }}>
          Growth of a deployed dollar
        </div>
        <div style={{ fontSize: font.sm, color: B.textMd, lineHeight: 1.6 }}>
          {/* Why the chart is empty, in words, and WHICH kind of empty — a
              missing index, capital older than its history, and a book with no
              marks recorded need three different things doing about them, and a
              blank plot area says none of them. */}
          {/* The path's own reason first: a comparison can be available (the
              card above has its multiples) while the PATH has nothing to draw —
              every flow before the chart's start, say — and the generic
              no-history sentence would then be untrue. */}
          {(path && path.reason)
            || (cmp && cmp.reason)
            || (benchmark && benchmark.reason)
            || 'No market history is available to price this book’s deployed capital against yet.'}
        </div>
      </Card>
    );
  }

  const innerW = Math.max(220, width - PLOT_PAD.left - PLOT_PAD.right);
  const innerH = PLOT_H - PLOT_PAD.top - PLOT_PAD.bottom;
  const tSpan = axis.tMax > 0 ? axis.tMax : 1;
  const vSpan = axis.max - axis.min || 1;
  const x = (t) => PLOT_PAD.left + (t / tSpan) * innerW;
  const y = (v) => PLOT_PAD.top + (1 - (v - axis.min) / vSpan) * innerH;

  const ours = (cmp && cmp.ours) || {};
  // TWO LINES, NOT THREE. On a dated path "unrealized" and "projected" are not
  // two curves — they are the same curve either side of today, which is exactly
  // what the measured/assumed split already draws. A third line would say the
  // same thing twice.
  const LINES = [
    {
      key: 'ours', label: 'Our deals', color: SERIES_HARVUS,
      note: 'marks where we have them, cost where we do not',
      rate: ours.unrealized || null,
    },
    {
      key: 'market', label: (cmp && cmp.name) || 'S&P 500', color: SERIES_MARKET,
      note: 'the same dollars, on the same dates, in the index',
      rate: (cmp && cmp.market) || null,
    },
  ];

  const last = series[series.length - 1];
  const ends = stackLabels(
    LINES.map((s) => ({ key: s.key, color: s.color, v: last ? last[s.key] : null }))
      .filter((s) => s.v != null)
      .map((s) => ({ key: s.key, color: s.color, y: y(s.v), text: formatGrowth(s.v) })),
    13,
  );

  // THE SPREAD BETWEEN THE TWO LINES IS THE CLAIM, so it is filled rather than
  // left to be measured by eye — above the index or below it is the one thing
  // this chart exists to answer, and the band answers it without a figure.
  const bandPath = (() => {
    const pts = series.filter((r) => r.ours != null && r.market != null);
    if (pts.length < 2) return null;
    const top = pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(2)},${y(p.ours).toFixed(2)}`).join('');
    const bot = pts.slice().reverse().map((p) => `L${x(p.t).toFixed(2)},${y(p.market).toFixed(2)}`).join('');
    return `${top}${bot}Z`;
  })();

  const at = hover != null ? series[hover] : null;
  const onMove = (ev) => {
    const box = ev.currentTarget.getBoundingClientRect();
    const t = ((ev.clientX - box.left - PLOT_PAD.left) / innerW) * tSpan;
    let best = null;
    let bestD = Infinity;
    series.forEach((r, i) => {
      const d = Math.abs(Number(r.t) - t);
      if (d < bestD) { bestD = d; best = i; }
    });
    setHover(best);
  };

  // Whole-year rows for the table twin — the same series a sighted reader sees,
  // at anchors rather than every monthly sample.
  const tableRows = ticks
    .map((tk) => series.reduce((a, r) => (r.t <= tk.t ? r : a), series[0]))
    .filter((r, i, arr) => r && arr.indexOf(r) === i);

  return (
    <Card style={{ padding: space.lg, marginBottom: space.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.md, flexWrap: 'wrap', marginBottom: space.md }}>
        <div>
          <div style={{ fontSize: font.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, fontWeight: weight.semibold }}>
            Growth of a deployed dollar
          </div>
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 3 }}>
            {/* WHAT THE COMPARISON IS, ON THE CARD. Every figure here rests on
                pricing our own capital into the index on the dates it actually
                went out, and a reader who has not been told that will read the
                market line as a generic index return over a window. */}
            Every dollar of realized capital, priced into{' '}
            <strong>{(cmp && cmp.name) || 'the index'} on the date it actually went out</strong>{' '}
            and valued at {cmp && cmp.asOf ? fmt.date(cmp.asOf) : 'the as-of date'} — against what the
            deals themselves are worth, cumulated over{' '}
            {cov ? <>{cov.matchedDeals} deal{cov.matchedDeals !== 1 ? 's' : ''}</> : 'the book'}
            {cmp && cmp.deployed != null && <> and {fmt.currency(cmp.deployed)} of capital</>}.
          </div>
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 3 }}>
            {/* Our side is a PORTFOLIO solve over the deals the comparison
                covers — one rate over the union of their flows, not an average
                of the deals' own rates. Said out loud because it used to be an
                average, and because it equals the table's "All deals" figure
                only where every deal is matched: the rate and the multiple
                beside it must cover the same dollars, so a deal the index
                cannot price is out of both. */}
            Our rates are one solve over the union of those deals&rsquo; flows — not an average of
            each deal&rsquo;s own rate
            {cov && cov.share != null && cov.share >= 0.999
              ? <>, and the same figure the &ldquo;All deals&rdquo; row shows in the Table view</>
              : <>. The &ldquo;All deals&rdquo; row in the Table view covers the whole book, including the
                deals left out below, so the two differ</>}.
          </div>
          {/* BOTH ENDS OF THE AXIS ARE DERIVED — the first realized outflow and
              the last modelled flow. There is no window to set, which is the
              correction the trailing-window chart needed.

              THE CHART IS THE WHOLE BOOK; THE SEARCH BOX IS NOT. The comparison
              is computed server-side over every deal the report loaded (the
              status filter reaches the server, so it does move it — the search
              box does not). Recomputing it client-side to follow the search
              would be a second implementation of it, which is the drift the
              "client mirrors nothing" rule exists to prevent. */}
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 3 }}>
            {/* A WINDOWED START IS SAID IN THE ENGINE'S WORDS. The report route
                anchors the path at the firm's chart start (October 2023), so
                the lines open at their growth to that date rather than at 0% —
                and a reader who has not been told that reads a line opening
                at +40% as a dollar that grew 40% on its first day. The client
                owns no copy of the rule; `path.windowNote` is rendered
                verbatim, and the derived-start sentence survives for a book
                whose first deployment is later than the anchor. */}
            {path.windowed && path.windowNote
              ? path.windowNote
              : <>From the first capital deployment{path.from ? ` (${fmt.date(path.from)})` : ''}.</>}
            {' '}Every deal on the report, whatever the search box in the table is narrowed to.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* The chart's own figures. A line chart is unreadable to a screen
              reader and to anyone who needs the numbers rather than the shape,
              and the same series at yearly anchors answers both.

              DELIBERATELY NOT LABELLED "table": the page's own Table view is
              the matrix, a different dataset entirely. */}
          <Btn variant="ghost" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>
            {asTable ? 'Show the curve' : 'Show the figures'}
          </Btn>
        </div>
      </div>

      {/* WHERE THE DOLLAR ENDED UP — the two multiples and the ratio between
          them, above the chart rather than inside its caption. The lines show
          the shape; these three figures ARE the answer, and on a dated path the
          lines pass exactly through them at the as-of date by construction
          rather than by a choice of horizon. */}
      {cmp && cmp.pme != null && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: space.lg, alignItems: 'baseline',
          padding: `${space.sm}px ${space.md}px`, marginBottom: space.md,
          background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: radius.md,
        }}>
          <MultipleFigure
            label="Our deals" value={ours.unrealized ? ours.unrealized.multiple : null}
            amount={ours.unrealized ? ours.unrealized.value : null} color={SERIES_HARVUS}
          />
          <MultipleFigure
            label={`In ${(cmp && cmp.name) || 'the index'}`} value={cmp.market ? cmp.market.multiple : null}
            amount={cmp.market ? cmp.market.value : null} color={SERIES_MARKET}
          />
          <div>
            <div style={{ fontSize: font.xs, color: B.textMd }}>
              Ahead / behind
              {/* A ratio is the kind of figure that gets quoted without its
                  basis, so the basis is in the label rather than the footnote. */}
              <span style={{ color: B.textLt }}> · at today&rsquo;s marks</span>
            </div>
            <div style={{
              fontSize: font.lg, fontWeight: weight.semibold, fontVariantNumeric: 'tabular-nums',
              color: cmp.pme >= 1 ? B.green : B.red,
            }}>
              {cmp.pme >= 1 ? '+' : '−'}{Math.abs((cmp.pme - 1) * 100).toFixed(1)}%
              <span style={{ fontSize: font.xs, fontWeight: weight.regular, color: B.textMd }}>
                {' '}vs the index
              </span>
            </div>
          </div>
        </div>
      )}

      {/* THE LEGEND IS ALWAYS PRESENT, so identity is never colour alone — and
          it carries the dash convention, which is a change of BASIS rather than
          a third party: solid is measured, dashed is an assumption a reader can
          change. */}
      <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap', marginBottom: space.md, fontSize: font.xs, color: B.textMd }}>
        {LINES.map((s) => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <svg width="18" height="8" aria-hidden style={{ display: 'block', flex: '0 0 auto' }}>
              <line x1="0" y1="4" x2="18" y2="4" stroke={s.color} strokeWidth="2" />
            </svg>
            <span>
              <strong style={{ color: B.text, fontWeight: weight.semibold }}>{s.label}</strong>{' '}
              {s.rate && s.rate.irr != null
                ? <>{formatRate(s.rate.irr)} a year</>
                : <span style={{ color: B.textLt }}>no rate</span>}
              {' '}<span style={{ color: B.textLt }}>· {s.note}</span>
            </span>
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="18" height="8" aria-hidden style={{ display: 'block', flex: '0 0 auto' }}>
            <line x1="0" y1="4" x2="18" y2="4" stroke={B.textLt} strokeWidth="2" strokeDasharray="5 4" />
          </svg>
          <span style={{ color: B.textLt }}>
            dashed past {path.asOf ? fmt.date(path.asOf) : 'today'} — assumed, not measured
          </span>
        </span>
      </div>

      {asTable ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: font.sm, minWidth: 460 }}>
            <caption style={{ captionSide: 'top', textAlign: 'left', fontSize: font.xs, color: B.textLt, paddingBottom: 6 }}>
              Cumulative growth on a deployed dollar, at each year end. Rows past {path.asOf ? fmt.date(path.asOf) : 'the as-of date'}
              {' '}rest on the assumed market return and on modelled exits.
            </caption>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 12px 4px 0', color: B.textMd, fontWeight: weight.semibold }}>Date</th>
                <th style={{ textAlign: 'right', padding: '4px 12px', color: B.textMd, fontWeight: weight.semibold }}>Deployed</th>
                {LINES.map((s) => (
                  <th key={s.key} style={{ textAlign: 'right', padding: '4px 12px', color: s.color, fontWeight: weight.semibold }}>{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => (
                <tr key={r.date}>
                  <td style={{ padding: '4px 12px 4px 0', color: B.text, whiteSpace: 'nowrap' }}>
                    {fmt.date(r.date)}{r.assumed ? <span style={{ color: B.textLt }}> · assumed</span> : null}
                  </td>
                  <td style={{ textAlign: 'right', padding: '4px 12px', color: B.textMd, fontVariantNumeric: 'tabular-nums' }}>
                    {fmt.currency(r.deployed)}
                  </td>
                  {LINES.map((s) => (
                    <td key={s.key} style={{ textAlign: 'right', padding: '4px 12px', color: B.text, fontVariantNumeric: 'tabular-nums' }}>
                      {/* A DASH IS NEVER A ZERO: a date the index cannot price
                          has no answer, and 0% is a claim that it broke even. */}
                      {formatGrowth(r[s.key]) || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={boxRef} style={{ position: 'relative' }}>
          <svg
            width={Math.max(220 + PLOT_PAD.left + PLOT_PAD.right, width)} height={PLOT_H}
            style={{ display: 'block', overflow: 'visible' }}
            role="img"
            aria-label={`Growth of a deployed dollar from ${path.from || 'the first deployment'} to ${path.to || 'today'}: ${LINES.map((s) => `${s.label} ${formatGrowth(last ? last[s.key] : null) || 'not shown'}`).join(', ')}`}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              {/* Clipped, so a line the axis cannot reach is cut at the frame
                  rather than drawn across the legend. */}
              <clipPath id="cfp-plot">
                <rect x={PLOT_PAD.left} y={PLOT_PAD.top} width={innerW} height={innerH} />
              </clipPath>
            </defs>

            {/* SOLID HAIRLINE GRIDLINES, NEVER DASHED — a dashed grid competes
                with a dashed series, and one of them is data. The zero line is
                stronger, because both lines start on it. */}
            {axis.ticks.map((t) => (
              <g key={t}>
                <line
                  x1={PLOT_PAD.left} x2={PLOT_PAD.left + innerW} y1={y(t)} y2={y(t)}
                  stroke={t === 0 ? B.border : B.divider} strokeWidth="1" shapeRendering="crispEdges"
                />
                <text x={PLOT_PAD.left - 8} y={y(t) + 3.5} textAnchor="end" fontSize="10" fill={t === 0 ? B.textMd : B.textLt}>
                  {formatGrowth(t, { decimals: axis.step >= 0.01 ? 0 : 1 }) || '0%'}
                </text>
              </g>
            ))}
            {ticks.map((tk) => (
              <text key={tk.label} x={x(tk.t)} y={PLOT_H - 8} textAnchor="middle" fontSize="10" fill={B.textLt}>
                {tk.label}
              </text>
            ))}

            <g clipPath="url(#cfp-plot)">
              {bandPath && <path d={bandPath} fill={SERIES_HARVUS} fillOpacity="0.08" stroke="none" />}
              {/* THE SEAM IS THE DASH, not a second colour: it is the same line
                  under a different basis, and a second hue would read as a
                  third party. */}
              {LINES.map((s) => (
                <g key={s.key}>
                  <path
                    d={pathOf(runs[s.key].solid, x, y)} fill="none"
                    stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                  />
                  <path
                    d={pathOf(runs[s.key].dashed, x, y)} fill="none"
                    stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                    strokeDasharray="5 4"
                  />
                </g>
              ))}
              {at && (
                <line
                  x1={x(at.t)} x2={x(at.t)} y1={PLOT_PAD.top} y2={PLOT_PAD.top + innerH}
                  stroke={B.textLt} strokeWidth="1" shapeRendering="crispEdges"
                />
              )}
            </g>

            {/* End markers and the direct labels the reference reads by. */}
            {LINES.map((s) => (last && last[s.key] != null ? (
              <circle key={s.key} cx={x(last.t)} cy={y(last[s.key])} r="4" fill={s.color} stroke={B.surface} strokeWidth="2" />
            ) : null))}
            {ends.map((e) => (
              <text key={e.key} x={PLOT_PAD.left + innerW + 10} y={e.y + 3.5} fontSize="11" fontWeight={weight.semibold} fill={B.text}>
                {e.text}
              </text>
            ))}

            {at && LINES.map((s) => (at[s.key] == null ? null : (
              <circle key={s.key} cx={x(at.t)} cy={y(at[s.key])} r="3.5" fill={B.surface} stroke={s.color} strokeWidth="2" />
            )))}
          </svg>

          {/* CROSSHAIR + TOOLTIP, the default on a line chart: the shape is the
              headline and the figure on a given date is the follow-up question,
              and the axis alone cannot answer it. */}
          {at && (
            <div style={{
              position: 'absolute', top: 0, pointerEvents: 'none',
              left: Math.min(Math.max(0, x(at.t) + 12), Math.max(0, width - 230)),
              background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.md,
              boxShadow: B.shadowMd, padding: '8px 10px', minWidth: 200, zIndex: 2,
            }}>
              <div style={{ fontSize: font.xs, color: B.textMd, fontWeight: weight.semibold, marginBottom: 4 }}>
                {fmt.date(at.date)}
                {at.assumed ? <span style={{ color: B.textLt, fontWeight: weight.regular }}> · assumed</span> : null}
              </div>
              {LINES.map((s) => (
                <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: font.xs, color: B.textMd, lineHeight: 1.7 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <svg width="14" height="8" aria-hidden style={{ display: 'block' }}>
                      <line x1="0" y1="4" x2="14" y2="4" stroke={s.color} strokeWidth="2" />
                    </svg>
                    {s.label}
                  </span>
                  <strong style={{ color: B.text, fontVariantNumeric: 'tabular-nums', fontWeight: weight.semibold }}>
                    {formatGrowth(at[s.key]) || '—'}
                  </strong>
                </div>
              ))}
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 4, borderTop: `1px solid ${B.divider}`, paddingTop: 4 }}>
                {fmt.currency(at.deployed)} deployed by this date
              </div>
            </div>
          )}
        </div>
      )}


      <div style={{ marginTop: space.md, fontSize: font.xs, color: B.textLt, lineHeight: 1.6 }}>
        {cmp && cmp.available ? (
          <>
            {/* THE CAVEATS ARE THE API'S OWN WORDS, verbatim. The client owns
                no copy of why this comparison is an approximation — a second
                copy would drift from the engine that knows. */}
            {(cmp.caveats || []).join(' ')}
            {/* COVERAGE, IN DOLLARS. A comparison speaking for a fifth of the
                book has to say so, and a deal count would not: one large
                unmatched deal matters and six tiny ones do not. */}
            {cov && cov.share != null && cov.share < 0.999 && (
              <> It covers {formatRate(cov.share)} of the book&rsquo;s realized capital
                ({fmt.currency(cov.deployed)} of {fmt.currency(cov.totalDeployed)}).
                {cov.beforeHistory > 0 && (
                  <> {cov.beforeHistory} deal{cov.beforeHistory !== 1 ? 's' : ''}{' '}
                    ({fmt.currency(cov.beforeHistoryDeployed)}) moved money before the index history
                    begins on {cmp.historyFrom ? fmt.date(cmp.historyFrom) : 'its first observation'}, so
                    there is no price to buy at and {cov.beforeHistory !== 1 ? 'they are' : 'it is'} left out
                    rather than priced at the earliest level available.</>
                )}
                {cov.costDeals > 0 && (
                  <> {cov.costDeals} of the {cov.matchedDeals} deal{cov.matchedDeals !== 1 ? 's' : ''} carr{cov.costDeals !== 1 ? 'y' : 'ies'} no
                    valuation mark and {cov.costDeals !== 1 ? 'are' : 'is'} in at cost or at a note&rsquo;s outstanding principal, so
                    no gain is credited on {cov.costDeals !== 1 ? 'them' : 'it'} — record a mark that is not flagged as a
                    forecast to change that.</>
                )}
              </>
            )}
          </>
        ) : (
          <>{(cmp && cmp.reason) || (benchmark && benchmark.reason) || 'No market history is available to compare these against.'}</>
        )}
      </div>
    </Card>
  );
}

/**
 * One side's answer to "where did the dollar end up" — the multiple, with the
 * dollars behind it underneath.
 *
 * The MULTIPLE leads and the amount follows, because the multiple is the
 * comparable figure and the amount is what makes it checkable. A null renders
 * as a dash and never as 1.0x, which would read as breaking even.
 */
function MultipleFigure({ label, value, amount, color }) {
  return (
    <div>
      <div style={{ fontSize: font.xs, color: B.textMd }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: color, marginRight: 6 }} aria-hidden />
        {label}
      </div>
      <div style={{ fontSize: font.lg, fontWeight: weight.semibold, fontVariantNumeric: 'tabular-nums', color: B.text }}>
        {value != null ? `${Number(value).toFixed(2)}×` : <span style={{ color: B.textLt }}>—</span>}
        {amount != null && (
          <span style={{ fontSize: font.xs, fontWeight: weight.regular, color: B.textMd }}>
            {' '}{fmt.currency(amount)}
          </span>
        )}
      </div>
    </div>
  );
}

// ---- the matrix ------------------------------------------------------------
// Hand-rolled rather than the shared <Table>: the columns are dynamic (one per
// period, up to hundreds), the first column has to stay put while the rest
// scroll, and rows expand into their capital items. `borderSpacing: 0` with
// per-cell borders rather than borderCollapse, because a collapsed border does
// not render reliably against a position:sticky cell.

const STICKY_W = 260;


const stickyCell = (extra = {}) => ({
  position: 'sticky', left: 0, zIndex: 2, background: B.surface,
  borderRight: `1px solid ${B.border}`, minWidth: STICKY_W, maxWidth: STICKY_W,
  padding: '9px 12px', textAlign: 'left', ...extra,
});

const numCell = (extra = {}) => ({
  padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap',
  borderBottom: `1px solid ${B.divider}`, ...extra,
});

function CashFlowMatrix({ report, rows, measure, showBefore, showAfter, hideZeroRows, onDealClick, onNoteClick }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const periods = report.periods || [];
  // A period that CLOSES on or before the cut-off holds realized cash only. The
  // period straddling today holds both and is deliberately not called actual —
  // it already renders as "current", and labelling a half-elapsed quarter as
  // fact is the mislabelling this shading exists to prevent.
  const asOf = report.asOf || null;
  const isHistory = (p) => !!(asOf && p && p.end && p.end <= asOf);
  // Only the PERIOD cells follow the chosen measure; the before/after and total
  // columns are always net, so they keep sign-based toning.
  const alwaysOut = measure === 'outflow';
  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const firstLabel = periods.length ? periods[0].label : '';
  const lastLabel = periods.length ? periods[periods.length - 1].label : '';

  const scrollerRef = useRef(null);
  const currentRef = useRef(null);
  const scrolledFor = useRef(null);
  const currentIdx = currentPeriodIndex(periods);

  // Scroll the current period under the sticky first column. Keyed on the AXIS
  // (its ends, its length and which column is current), so it re-runs when the
  // granularity or the date window changes and stays put for everything else.
  const axisKey = periods.length
    ? `${periods[0].key}|${periods[periods.length - 1].key}|${periods.length}|${currentIdx}`
    : '';
  useEffect(() => {
    if (!axisKey || scrolledFor.current === axisKey) return;
    const sc = scrollerRef.current;
    const cell = currentRef.current;
    if (!sc || !cell) return;
    // Measured rather than summed from column widths: the columns are sized by
    // their content, so any arithmetic here would be a second layout engine.
    const left = cell.getBoundingClientRect().left - sc.getBoundingClientRect().left
      + sc.scrollLeft - STICKY_W;
    sc.scrollLeft = Math.max(0, left);
    scrolledFor.current = axisKey;
  }, [axisKey]);

  const headCell = (extra = {}) => ({
    padding: '10px 12px', textAlign: 'right', fontSize: font.xs, color: B.textMd,
    fontWeight: weight.semibold, letterSpacing: 0.4, textTransform: 'uppercase',
    whiteSpace: 'nowrap', borderBottom: `1px solid ${B.border}`, background: B.surfaceMuted, ...extra,
  });

  return (
    <div ref={scrollerRef} style={{
      overflowX: 'auto', border: `1px solid ${B.border}`, borderRadius: radius.xl2,
      background: B.surface, boxShadow: B.shadowXs,
    }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: font.base, minWidth: '100%' }}>
        <thead>
          <tr>
            <th scope="col" style={headCell({ ...stickyCell({ zIndex: 3, background: B.surfaceMuted }), textAlign: 'left' })}>Deal</th>
            {showBefore && <th scope="col" style={headCell({ color: B.textLt })} title={`Projected cash flow dated before ${firstLabel}`}>Before {firstLabel}</th>}
            {periods.map((p, i) => (
              <th
                key={p.key}
                scope="col"
                ref={i === currentIdx ? currentRef : undefined}
                style={headCell(i === currentIdx
                  ? { color: B.navy, background: B.tealLt }
                  : (isHistory(p) ? { background: B.surface } : {}))}
                title={i === currentIdx
                  ? `${p.start} to ${p.end} — the current period`
                  : `${p.start} to ${p.end}${isHistory(p) ? ' — realized, from the Sage ledger' : ''}`}
              >
                {p.label}
                {isHistory(p) && (
                  <div style={{ fontSize: font.xs, color: B.green, fontWeight: weight.regular, letterSpacing: 0, textTransform: 'none' }}>actual</div>
                )}
              </th>
            ))}
            {showAfter && <th scope="col" style={headCell({ color: B.textLt })} title={`Projected cash flow dated after ${lastLabel}`}>After {lastLabel}</th>}
            <th scope="col" style={headCell({ borderLeft: `1px solid ${B.border}` })}>Total net</th>
            {/* THE TWO RATES SIT BESIDE THE TOTAL because that is the pair a
                reader compares: what the money adds up to, and what rate it
                implies. They are LABELLED rather than headed "IRR" twice —
                unrealized sells today at what the position is carried at,
                projected holds it to plan, and one unqualified rate on a screen
                gets quoted as whichever the reader assumed. */}
            <th scope="col" style={headCell({ borderLeft: `1px solid ${B.border}` })}
              title="Realized Sage cash, then a sale TODAY at what the position is carried at today — mark to market. A dash means it cannot be solved from what is recorded; hover the dash for the reason.">
              Unrealized IRR
            </th>
            <th scope="col" style={headCell()}
              title="Realized Sage cash, then every remaining flow this report projects, the exit included — the return if the position is held to plan.">
              Projected IRR
            </th>
          </tr>
        </thead>
        <tbody>
          {/* THE PORTFOLIO TOTAL LEADS. It is the number most readers came for,
              and putting it above a hundred scrolling rows means it is visible
              without hunting for the bottom of the table. */}
          <tr style={{ background: B.surfaceMuted }}>
            <th scope="row" style={stickyCell({
              background: B.surfaceMuted, borderBottom: `2px solid ${B.border}`,
              fontWeight: weight.semibold, color: B.navyDk, fontSize: font.base,
            })}>
              All deals
              <div style={{ fontSize: font.xs, color: B.textMd, fontWeight: weight.regular, marginTop: 2 }}>
                {report.coverage?.projected ?? 0} of {report.coverage?.deals ?? 0} projected
              </div>
            </th>
            {showBefore && <td style={numCell({ background: B.surfaceMuted, borderBottom: `2px solid ${B.border}` })}><MoneyCell value={report.before?.net} /></td>}
            {periods.map((p, i) => (
              <td key={p.key} style={numCell({ background: B.surfaceMuted, borderBottom: `2px solid ${B.border}` })}>
                <MoneyCell value={report.series?.[measure]?.[i]} strong alwaysOut={alwaysOut} />
              </td>
            ))}
            {showAfter && <td style={numCell({ background: B.surfaceMuted, borderBottom: `2px solid ${B.border}` })}><MoneyCell value={report.after?.net} /></td>}
            <td style={numCell({ background: B.surfaceMuted, borderBottom: `2px solid ${B.border}`, borderLeft: `1px solid ${B.border}` })}>
              <MoneyCell value={report.totals?.net} strong />
            </td>
            {/* THE BOOK'S RATES ARE SOLVED OVER EVERY CAPITAL ITEM'S FLOWS AT
                ONCE, not averaged from the deal rates below — a rate is not
                additive, and a weighted average of IRRs over different periods
                is a different number that looks just as plausible. */}
            <td style={numCell({ background: B.surfaceMuted, borderBottom: `2px solid ${B.border}`, borderLeft: `1px solid ${B.border}` })}>
              <RateCell block={report.irr?.unrealized} strong onNoteClick={onNoteClick} />
            </td>
            <td style={numCell({ background: B.surfaceMuted, borderBottom: `2px solid ${B.border}` })}>
              <RateCell block={report.irr?.projected} strong onNoteClick={onNoteClick} />
            </td>
          </tr>

          {rows.map(deal => {
            const open = expanded.has(deal.dealID);
            const shownDetails = hideZeroRows
              ? (deal.details || []).filter(item => !rowIsAllZero(item, measure))
              : (deal.details || []);
            const multi = shownDetails.length > 1;
            return [
              <tr key={deal.dealID}>
                <th scope="row" style={stickyCell({ borderBottom: `1px solid ${B.divider}`, fontWeight: weight.regular })}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    {multi ? (
                      <button onClick={() => toggle(deal.dealID)} aria-expanded={open}
                        aria-label={open ? `Collapse ${deal.name}` : `Expand ${deal.name}`}
                        style={{ background: 'none', border: 'none', padding: 0, marginTop: 2, cursor: 'pointer', color: B.textMd, display: 'flex' }}>
                        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} />
                      </button>
                    ) : <span style={{ width: 14, flexShrink: 0 }} />}
                    <div style={{ minWidth: 0 }}>
                      {/* HOVERING THE DEAL NAME EXPLAINS THE ROW. Everything the
                          row used to carry as chips — which model spoke for each
                          capital item, its components, the mark, the debt that
                          came off the sale, a matured note — is in the card, laid
                          out where it can be read rather than compressed into
                          four words and a title attribute. */}
                      <HoverInfo card={<DealMethodCard deal={deal} />} label={`How ${deal.name}’s figures were arrived at`}>
                        {onDealClick ? (
                          <button onClick={() => onDealClick(deal.dealID)} style={{
                            background: 'none', border: 'none', padding: 0, color: B.navy, cursor: 'pointer',
                            fontFamily: 'inherit', fontSize: font.base, fontWeight: weight.semibold,
                            textAlign: 'left', textDecoration: 'underline',
                          }}>{deal.name}</button>
                        ) : <span style={{ fontWeight: weight.semibold }}>{deal.name}</span>}
                      </HoverInfo>
                      <div style={{ marginTop: 3, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* The one bubble left: what KIND of capital this is. */}
                        <TypeBadges types={deal.detailTypes} />
                        {!deal.projected && (
                          <span style={{ fontSize: font.xs, color: B.textLt, fontStyle: 'italic' }}>
                            {/* "not projected" on a row showing $1.7M of real cash reads as
                                a bug. It has a ledger and no forecast, and those are two
                                different sentences — but the reason WHY is a note now
                                collected below, not a fragment floating on the row. */}
                            {deal.hasActuals ? 'actuals only — nothing projected forward' : 'not projected'}
                            <NoteMark onClick={onNoteClick} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </th>
                {showBefore && <td style={numCell()}><MoneyCell value={deal.before?.net} /></td>}
                {periods.map((p, i) => (
                  <td key={p.key} style={numCell()}><MoneyCell value={deal.series?.[measure]?.[i]} alwaysOut={alwaysOut} /></td>
                ))}
                {showAfter && <td style={numCell()}><MoneyCell value={deal.after?.net} /></td>}
                <td style={numCell({ borderLeft: `1px solid ${B.border}` })}><MoneyCell value={deal.totals?.net} strong /></td>
                <td style={numCell({ borderLeft: `1px solid ${B.border}` })}><RateCell block={deal.irr?.unrealized} onNoteClick={onNoteClick} /></td>
                <td style={numCell()}><RateCell block={deal.irr?.projected} onNoteClick={onNoteClick} /></td>
              </tr>,
              // A capital item that shows nothing is the same noise one row up,
              // so the filter reaches the expanded rows too. It can never make
              // the children stop tying to their parent: what it removes is
              // zero.
              ...(open ? shownDetails.map(item => (
                <tr key={`${deal.dealID}:${item.dealDetailID}`} style={{ background: B.hover }}>
                  <th scope="row" style={stickyCell({ background: B.hover, borderBottom: `1px solid ${B.divider}`, fontWeight: weight.regular, paddingLeft: 32 })}>
                    {/* A CAPITAL ITEM GETS ITS OWN CARD, not the deal's. On a
                        multi-item deal the deal card is every item's method in
                        one panel; here it is only this item's, which is what a
                        reader who has expanded the row is asking about. */}
                    <HoverInfo card={<MethodSection basis={item.basis} name={item.label} showName />}
                      label={`How ${item.label}’s figures were arrived at`}>
                      <span style={{ fontSize: font.sm, color: B.text }}>{item.label}</span>
                    </HoverInfo>
                    <div style={{ marginTop: 3, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TypeBadges types={item.detailType ? [item.detailType] : []} />
                      {/* A DEAL-LEVEL SAGE ENTITY says whose money it is and how it
                          got here, in place of the engine's "no budget, capital
                          schedule or valuation" — true of every holding company
                          ever attached, and not the sentence a reader needs. */}
                      {item.entity ? (
                        <span style={{ fontSize: font.xs, color: B.textLt, fontStyle: 'italic' }}>
                          Sage entity {item.entity.code ? `· ${item.entity.code}` : ''} · actuals only
                          <NoteMark onClick={onNoteClick} />
                        </span>
                      ) : item.reason && (
                        <span style={{ fontSize: font.xs, color: B.textLt, fontStyle: 'italic' }}>
                          not projected
                          <NoteMark onClick={onNoteClick} />
                        </span>
                      )}
                    </div>
                  </th>
                  {showBefore && <td style={numCell({ background: B.hover })}><MoneyCell value={item.before?.net} /></td>}
                  {periods.map((p, i) => (
                    <td key={p.key} style={numCell({ background: B.hover })}><MoneyCell value={item.series?.[measure]?.[i]} alwaysOut={alwaysOut} /></td>
                  ))}
                  {showAfter && <td style={numCell({ background: B.hover })}><MoneyCell value={item.after?.net} /></td>}
                  <td style={numCell({ background: B.hover, borderLeft: `1px solid ${B.border}` })}><MoneyCell value={item.totals?.net} /></td>
                  <td style={numCell({ background: B.hover, borderLeft: `1px solid ${B.border}` })}><RateCell block={item.irr?.unrealized} onNoteClick={onNoteClick} /></td>
                  <td style={numCell({ background: B.hover })}><RateCell block={item.irr?.projected} onNoteClick={onNoteClick} /></td>
                </tr>
              )) : []),
            ];
          })}

          {rows.length === 0 && (
            <tr>
              <td colSpan={(periods.length || 1) + 4 + (showBefore ? 1 : 0) + (showAfter ? 1 : 0)}
                style={{ padding: '40px 14px', textAlign: 'center', color: B.textMd }}>
                {hideZeroRows ? 'Every deal matching this filter is empty in this view.' : 'No deals match this filter.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---- the page --------------------------------------------------------------

export const CashFlowReport = ({ onDealClick, user }) => {
  const [granularity, setGranularity] = useState('quarter');
  const [measure, setMeasure] = useState('net');
  const [status, setStatus] = useState('All');
  // WHICH CAPITAL ITEMS. Sets of what is EXCLUDED (see CheckGroup for why);
  // the hold-type options come from the server, which reads what the book
  // actually holds, and are kept in their own state so they survive a run that
  // failed to read them and so the query string has something stable to
  // derive the included list from.
  const [excludedTypes, setExcludedTypes] = useState(() => new Set());
  const [excludedHoldTypes, setExcludedHoldTypes] = useState(() => new Set());
  const [holdOptions, setHoldOptions] = useState([]);
  const [from, setFrom] = useState('');
  // THE VINTAGE — keep only deals first deployed on or after this date. A
  // FILTER on which deals are in the run (it moves every rate, both views and
  // the workbook), where `from`/`to` above are the table's axis and drop
  // nothing. Sent only once complete, like the window dates.
  const [deployedFrom, setDeployedFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  // Table or chart — never both (see VIEWS).
  const [view, setView] = useState('table');
  // THE FORWARD MARKET ASSUMPTION, as a PERCENT — the one input on this page
  // that changes a figure rather than filtering one, so it reaches the server
  // (the path is computed there; recomputing it here would be a second
  // implementation of the comparison). Empty means "the engine's default", NOT
  // zero: a flat market is a claim and an unset control must never make it.
  const [growthPct, setGrowthPct] = useState('');
  // Hide the rows that are entirely empty. Off by default: a deal showing
  // nothing is itself a finding — it has no budget, no schedule, no dated mark
  // and no ledger — and silently dropping it would make the book look smaller
  // than it is. See `rowIsAllZero` for what counts as empty.
  const [hideZeroRows, setHideZeroRows] = useState(false);
  // THE OWNER ENTITIES — WHO OWNS EACH DEAL, read off the Sage location
  // hierarchy: a deal belongs to the entity at the root of the tree its
  // tagged locations sit in (Turkey Lake, LLC is the parent of ~51 locations
  // across ~28 deals, so ticking it alone keeps those 28). ONE CHECKBOX PER
  // ENTITY: the list comes off the server (`ownerOptions`, read unnarrowed by
  // this filter so an unticked owner stays offered, with a count of the run's
  // deals under each), plus a "No Sage entity" row for the deals under none.
  // The state is what is EXCLUDED, like the capital types, so an entity that
  // gains a deal tomorrow is in by default and an older API that ignores the
  // narrowing keeps every deal in. What travels is the KEPT codes
  // (`ownerEntities`), nothing when every box is ticked. This replaced a list
  // of the entities ATTACHED to a deal (their own ledgers, dbo.DealSageEntities)
  // — a different question, and one on which Turkey Lake, attached to none of
  // its 28 deals by the one-entity-one-deal rule, was not an option at all.
  // The attached ledgers still ride along WITH their deals.
  const [excludedOwners, setExcludedOwners] = useState(() => new Set());
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerNone, setOwnerNone] = useState(null);
  // The filter panel is CLOSED by default; its header always says what is in
  // force (see FilterDisclosure).
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The report's caveats are CLOSED by default too, for the same reason (see
  // ReportNotesDisclosure) — they used to stand open between the KPI strip
  // and the table; now they collapse at the foot of the page instead.
  const [notesOpen, setNotesOpen] = useState(false);
  // WHAT EVERY `*` MARK ON THE PAGE POINTS AT. A mark on a row is only useful
  // if it actually gets the reader to the sentence it stands for, so clicking
  // one opens the panel AND scrolls it into view — `requestAnimationFrame`
  // lets the panel actually expand (and the scroll container settle) before
  // the browser measures where to scroll to.
  const notesRef = useRef(null);
  const openRowNotes = () => {
    setNotesOpen(true);
    requestAnimationFrame(() => notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  // The export runs server-side and reads every source in the book, so it takes
  // seconds. A pending flag and a result line are the whole UI: a button that
  // looks idle while a 60,000-row workbook is being built reads as one that did
  // nothing, and gets pressed again.
  // const [exporting, setExporting] = useState(false);  // PARKED, see runEventExport below
  const [exportMsg, setExportMsg] = useState(null);

  // The workbook export runs server-side and takes a while, so the button says
  // so rather than looking dead; a failure is SHOWN rather than swallowed — a
  // download that silently does not happen reads as a broken product.
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [xlsxError, setXlsxError] = useState(null);

  // `from`/`to` are only sent once complete — a half-typed date would be
  // refused by the API on every keystroke.
  const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

  // THE INCLUDED LISTS THE API TAKES, derived once and used by BOTH the report
  // request and the export — so the workbook cannot cover a different set of
  // capital items from the screen it was launched from. Null means "no
  // narrowing" and is not sent. A hold-type narrowing is only meaningful while
  // R/E is in the run (it narrows real estate and nothing else — see
  // api/src/capitalTypeFilter.js), so it is dropped when R/E is out.
  const holdChoices = useMemo(
    () => [...holdOptions, NO_HOLD_TYPE],
    [holdOptions],
  );
  const typeSelection = useMemo(() => {
    const detailTypes = excludedTypes.size ? DETAIL_TYPES.filter((t) => !excludedTypes.has(t)) : null;
    const reIn = !detailTypes || detailTypes.includes('R/E');
    const holdTypes = reIn && excludedHoldTypes.size
      ? holdChoices.filter((h) => !excludedHoldTypes.has(h))
      : null;
    return { detailTypes, holdTypes: holdTypes && holdTypes.length ? holdTypes : null };
  }, [excludedTypes, excludedHoldTypes, holdChoices]);
  // WHICH OWNERS THE API IS SENT: nothing when every one is in (the default,
  // and what an older API expects), else the KEPT codes — 'none' standing for
  // the deals under no entity. Derived from the OPTIONS, so an excluded code
  // that no longer owns a deal in the run drops out of the arithmetic rather
  // than being sent. Only the offered rows (those owning a deal inside the
  // run's other filters) enter it; a roster entity owning none is on the list
  // so its absence has a reason beside it, and is never sent.
  const offeredOwners = useMemo(() => ownerOptions.filter((o) => o.offered), [ownerOptions]);
  const unofferedOwners = useMemo(() => ownerOptions.filter((o) => !o.offered), [ownerOptions]);
  const ownerNoneOffered = !!(ownerNone && ownerNone.deals > 0);
  const ownerChoiceValues = useMemo(() => [
    ...offeredOwners.map((o) => o.code),
    ...(ownerNoneOffered ? [NO_OWNER] : []),
  ], [offeredOwners, ownerNoneOffered]);
  const ownerEntities = useMemo(() => {
    if (!ownerChoiceValues.length || !excludedOwners.size) return null;
    const kept = ownerChoiceValues.filter((v) => !excludedOwners.has(v));
    if (!kept.length || kept.length === ownerChoiceValues.length) return null;
    return kept;
  }, [ownerChoiceValues, excludedOwners]);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ granularity });
    if (status !== 'All') p.set('status', status);
    if (typeSelection.detailTypes) p.set('detailTypes', typeSelection.detailTypes.join(','));
    if (typeSelection.holdTypes) p.set('holdTypes', typeSelection.holdTypes.join(','));
    if (ownerEntities) p.set('ownerEntities', ownerEntities.join(','));
    if (isDate(from)) p.set('from', from);
    if (isDate(to)) p.set('to', to);
    if (isDate(deployedFrom)) p.set('deployedFrom', deployedFrom);
    // Named for its UNIT. The engine takes a fraction and refuses anything past
    // ±50%, so a percent sent bare would read as 650% a year, fail the range
    // check and silently revert to the default — a control that looks set and
    // does nothing (the conversion is in buildCashFlowProjectionReport).
    if (growthPct !== '') p.set('forwardGrowthPct', growthPct);
    return p.toString();
  }, [granularity, status, from, to, deployedFrom, growthPct, typeSelection, ownerEntities]);

  const { data, loading, error, reload } = useApi(
    () => api.get(`reports/cash-flow-projection?${qs}`,
                  { timeout: REPORT_TIMEOUT_MS }), [qs]);

  // The hold types the book holds, off the server. Null means that read
  // failed and the control keeps whatever it last knew rather than emptying.
  // Setting state here cannot loop: the query string is a STRING, so a fresh
  // array of the same options re-derives the same string and useApi does not
  // refetch on it.
  useEffect(() => {
    if (data && Array.isArray(data.holdTypeOptions)) {
      const next = data.holdTypeOptions;
      setHoldOptions((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
    }
    // The entities the run could carry, off the server — read unnarrowed and
    // even when they are switched off, so the list is the way back. Null means
    // that read failed and the list keeps whatever it last knew; a database
    // without migration 182 never sends one. Compared by content, so the same
    // list re-derives the same state and nothing loops.
    if (data && Array.isArray(data.ownerOptions)) {
      const next = data.ownerOptions.filter((o) => o && o.code);
      const sig = (list) => list.map((o) => `${o.code}|${o.name || ''}|${o.deals}|${o.dealsInScope}|${o.offered ? 1 : 0}|${o.reason || ''}`).join('\n');
      setOwnerOptions((prev) => (sig(prev) === sig(next) ? prev : next));
      const none = data.ownerNone && typeof data.ownerNone === 'object' ? data.ownerNone : null;
      setOwnerNone((prev) => (JSON.stringify(prev) === JSON.stringify(none) ? prev : none));
    }
  }, [data]);

  const toggleType = (t) => setExcludedTypes((prev) => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });
  const toggleHoldType = (h) => setExcludedHoldTypes((prev) => {
    const next = new Set(prev);
    if (next.has(h)) next.delete(h); else next.add(h);
    return next;
  });
  const reIncluded = !typeSelection.detailTypes || typeSelection.detailTypes.includes('R/E');
  const toggleOwner = (code) => setExcludedOwners((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });
  // All / None for the owner list. None excludes every code on offer BUT the
  // last, since the owners partition the deals and an empty partition is an
  // empty report with nothing on it to say why (the capital-type rule); a
  // later option list can put a newly owning entity back in by default.
  const setAllOwners = (on) => setExcludedOwners(on
    ? new Set()
    : new Set(ownerChoiceValues.slice(0, Math.max(0, ownerChoiceValues.length - 1))));
  // Offered once the server has sent a list at all — a database without the
  // Sage masters never does. A list of nothing but unoffered roster entities is
  // still offered, because "none of these owns a deal in this run" is the
  // answer to the question the reader opened the dropdown with.
  const ownerOffered = ownerOptions.length > 0 || ownerNoneOffered;
  const dealsWord = (n) => `${n} deal${n === 1 ? '' : 's'}`;
  const ownerChoices = useMemo(() => [
    ...offeredOwners.map((o) => ({
      value: o.code,
      label: o.name || o.code || 'Unnamed entity',
      hint: [o.name ? o.code : null, dealsWord(o.deals)].filter(Boolean).join(' · '),
    })),
    ...(ownerNoneOffered ? [{
      value: NO_OWNER,
      label: OWNER_NONE_LABEL,
      hint: `${dealsWord(ownerNone.deals)} whose Sage locations roll up to no entity, or with no Sage tagging`,
    }] : []),
  ], [offeredOwners, ownerNoneOffered, ownerNone]);
  // THE ROSTER ENTITIES THAT CANNOT BE TICKED, each with the reason: it owns
  // deals the run's other filters left out (widen them), or no tagged Sage
  // location in the book rolls up to it at all (a fact about Sage, not about
  // this report). "Why isn't Turkey Lake in this list" is the question this
  // control was rebuilt to answer, so an entity is never left off in silence.
  const ownerExtras = useMemo(() => unofferedOwners.map((o) => {
    const name = o.name || o.code;
    const outside = o.reason === 'outsideRun';
    return {
      value: `roster:${o.code}`,
      label: o.name || o.code || 'Unnamed entity',
      hint: outside ? `owns ${dealsWord(o.dealsInScope)} outside this run\u2019s filters` : 'owns no tagged location',
      reason: outside
        ? `${name} owns ${dealsWord(o.dealsInScope)} this run leaves out — a status the Status filter excludes. Widen the filters to bring them in.`
        : `No Sage location tagged on a capital item rolls up to ${name} in the location hierarchy, so it owns no deal on this report. Tag a deal's capital item to one of its locations (or set a location's parent to ${o.code} in Sage) and it will appear here.`,
    };
  }), [unofferedOwners]);

  // WHAT IS NARROWING THE RUN, for the summary line — each item with its own
  // clear. Only the filters that reach the server: the search box narrows the
  // rendered table and says so on the export button; resolution, measure and
  // Hide 0 rows are how the table is read, not which deals are in it.
  const clearFilters = () => {
    setStatus('All');
    setExcludedTypes(new Set());
    setExcludedHoldTypes(new Set());
    setExcludedOwners(new Set());
    setFrom('');
    setTo('');
    setDeployedFrom('');
  };
  const activeFilterItems = useMemo(() => {
    const items = [];
    if (status !== 'All') items.push({ key: 'status', label: `Status: ${status}`, onClear: () => setStatus('All') });
    if (typeSelection.detailTypes) {
      items.push({ key: 'types', label: `Capital types: ${typeSelection.detailTypes.map(shortOf).join(', ')}`, onClear: () => setExcludedTypes(new Set()) });
    }
    if (typeSelection.holdTypes) {
      const words = typeSelection.holdTypes.map((h) => (h === NO_HOLD_TYPE ? HOLD_TYPE_NONE_LABEL : h));
      items.push({ key: 'hold', label: `R/E hold: ${words.join(', ')}`, onClear: () => setExcludedHoldTypes(new Set()) });
    }
    if (ownerEntities) {
      const names = ownerEntities.map((c) => (c === NO_OWNER
        ? OWNER_NONE_LABEL
        : ((offeredOwners.find((o) => o.code === c) || {}).name || c)));
      items.push({
        key: 'owners',
        label: names.length <= 3
          ? `Owner: ${names.join(', ')}`
          : `Owners: ${ownerEntities.length} of ${ownerChoiceValues.length}`,
        onClear: () => setExcludedOwners(new Set()),
      });
    }
    if (isDate(deployedFrom)) items.push({ key: 'deployedFrom', label: `Deployed from ${fmt.date(deployedFrom)}`, onClear: () => setDeployedFrom('') });
    if (isDate(from)) items.push({ key: 'from', label: `From ${fmt.date(from)}`, onClear: () => setFrom('') });
    if (isDate(to)) items.push({ key: 'to', label: `To ${fmt.date(to)}`, onClear: () => setTo('') });
    return items;
    // `isDate` is a stable inline helper; `shortOf` / `fmt` are module imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, typeSelection, ownerEntities, offeredOwners, ownerChoiceValues.length, from, to, deployedFrom]);

  // THE FILE MUST DESCRIBE THE RUN ON SCREEN, so it is sent the filters that
  // reached the SERVER, plus the MEASURE — which is client-side but decides
  // which series fills the Matrix sheet's cells, so a file written at a measure
  // other than the one on screen would disagree with the table it came from.
  //
  // The search box and "hide 0 rows" are deliberately NOT sent: a workbook of
  // underlying flows narrowed by a text match would be a subset nothing in the
  // file explains, and the button's tooltip says the whole run is what comes
  // back.
  const runXlsxExport = async () => {
    setXlsxBusy(true);
    setXlsxError(null);
    setExportMsg(null);
    try {
      const res = await exportCashFlowXlsx({
        granularity, status, from, to, measure,
        // The SAME narrowing the report on screen was run with.
        detailTypes: typeSelection.detailTypes,
        holdTypes: typeSelection.holdTypes,
        ownerEntities,
        deployedFrom: isDate(deployedFrom) ? deployedFrom : null,
      });
      // A TRUNCATED WORKBOOK IS THE ONE OUTCOME THE FILE ITSELF CANNOT REPORT,
      // so the banner says it. A clean run is reported too, because the
      // download arrives silently and a reader otherwise cannot tell a
      // finished export from one that never started.
      setExportMsg({ tone: res?.truncated ? 'warn' : 'ok', text: describeCashFlowWorkbook(res) });
    } catch (e) {
      setXlsxError(e?.message || 'The workbook could not be exported.');
    } finally {
      setXlsxBusy(false);
    }
  };

  const report = data || null;

  const rows = useMemo(() => {
    if (!report || !report.deals) return [];
    const needle = q.trim().toLowerCase();
    return report.deals.filter(d => {
      if (hideZeroRows && rowIsAllZero(d, measure)) return false;
      return dealMatchesQuery(d, needle);
    });
  }, [report, q, hideZeroRows, measure]);

  // EVERY `*` ON THE TABLE, COLLECTED HERE — a deal's "not projected" reason,
  // a capital item's Sage-entity note, the caveat behind a deal's rate. See
  // collectRowNotes for why these are grouped by their own text rather than
  // repeated once per row, and mirror exactly what `rows` (this same,
  // already-filtered list) puts on screen.
  const rowNotes = useMemo(
    () => collectRowNotes(rows, measure, hideZeroRows),
    [rows, measure, hideZeroRows],
  );

  // Before / after columns appear only when they carry money — an all-zero
  // column is noise, but hiding a non-zero one would let a total disagree with
  // the row it sits on.
  const showBefore = !!(report && (report.before?.net || rows.some(r => r.before?.net)));
  const showAfter = !!(report && (report.after?.net || rows.some(r => r.after?.net)));

  const unavailableSources = report && report.sources
    ? Object.entries(report.sources).filter(([, v]) => !v.available)
    : [];

  // THE SAME FILTERS THE REPORT WAS RUN WITH, so the workbook covers exactly
  // what is on screen — except the search box and Hide 0 rows, which narrow the
  // rendered table client-side and have no server equivalent. That is stated to
  // the user in the button's tooltip rather than left to be discovered from a
  // file that turned out to be wider than the table above it.
  // PARKED — the 4-sheet event export from the parallel implementation. The
  // live export is `runXlsxExport` above, which writes those flows plus the
  // terminals, the per-deal index comparison and every rate, with the
  // arithmetic as LIVE FORMULAS rather than as values. Kept in full rather
  // than deleted: its Items sheet (one row per capital item, both IRRs and the
  // exit gross/debt/net side by side) and its Grid carrying ALL FOUR measures
  // at once are both worth folding into the live workbook, and neither exists
  // there yet. Re-wire by restoring this and its button.
  // const runEventExport = async () => {
  //   setExporting(true);
  //   setExportMsg(null);
  //   try {
  //     const res = await exportCashFlowEventsXlsx({
  //       granularity,
  //       from: isDate(from) ? from : null,
  //       to: isDate(to) ? to : null,
  //       status: status !== 'All' ? status : null,
  //     });
  //     setExportMsg({ tone: res?.truncated ? 'warn' : 'ok', text: describeCashFlowExport(res) });
  //   } catch (e) {
  //     // The API's own refusal, verbatim — it names which capital items reported
  //     // a reason instead of a projection, which is the actionable half.
  //     setExportMsg({ tone: 'err', text: e?.message || 'The export failed.' });
  //   } finally { setExporting(false); }
  // };

  const granularityWord = (GRANULARITIES.find(g => g.value === granularity) || {}).label || '';

  // EVERY CAVEAT ABOUT THIS RUN, COLLECTED HERE RATHER THAN RENDERED WHERE ITS
  // CONDITION IS TESTED. They are still every bit as real — a caveat that only
  // shows on request is one the reader has to know to ask for — just collected
  // into one list and handed to ReportNotesDisclosure, which renders them
  // collapsed at the foot of the page instead of stacked open between the KPI
  // strip and the table. FOUR ARE ABOUT ONE VIEW and stay gated to it: the
  // period cap, the measure and the empty rows are facts about the matrix's
  // columns and cells, and the missing market line is a fact about the chart.
  // Everything else (the debt netting, the ledger, an unreadable source)
  // changes the rates AND the rows, so it applies in both.
  //
  // `rowNotes` joins this same list at the end (below), so a REPORT-WIDE note
  // (a narrowing filter, a truncated axis) and a ROW-level one (why one deal
  // isn't projected, a deal's rate caveat) sit in the one place a reader
  // already knows to look — the `*` on a row and the ⚠/i on the KPIs both
  // point here.
  const reportNotes = [];
  if (report && report.available) {
    // WHAT THE NARROWING LEFT OUT, in the engine's words and in BOTH views —
    // it qualifies every figure on the page, the chart's curves included.
    // Rendered verbatim so the page and the workbook's README say the same
    // sentence.
    if (report.typeFilterNote) {
      reportNotes.push({ key: 'typeFilter', tone: 'warn', body: report.typeFilterNote });
    }
    // THE ENTITY SWITCH, in the engine's words, for the same reason: a run
    // without the holding companies reads a levered deal as unlevered, and the
    // workbook README says the same sentence.
    if (report.ownerFilterNote) {
      reportNotes.push({ key: 'ownerFilter', tone: 'warn', body: report.ownerFilterNote });
    }
    // THE VINTAGE, in the engine's words, plus the deals it left out with the
    // date each was first deployed on — a narrowed run has to be able to say
    // why a deal is missing, and the answer only lives on the deals that are
    // out. Verbatim, so the page and the workbook README say the same sentence.
    if (report.deployedFromNote) {
      reportNotes.push({
        key: 'deployedFrom',
        tone: 'warn',
        body: (
          <>
            {report.deployedFromNote}
            {Array.isArray(report.deployedFromExcluded) && report.deployedFromExcluded.length > 0 && (
              <span style={{ display: 'block', marginTop: 4, color: B.textMd }}>
                Left out: {report.deployedFromExcluded.map((d) => `${d.name || d.dealID} (first deployed ${fmt.date(d.firstDeployment)})`).join(' · ')}
              </span>
            )}
          </>
        ),
      });
    }
    if (report.entityFilterNote) {
      reportNotes.push({ key: 'entityFilter', tone: 'warn', body: report.entityFilterNote });
    }
    // A LOST ENTITY READ reads exactly like a book whose holding companies
    // carry no money, so it is never silent.
    if (report.entitiesIncluded && report.entitiesAvailable === false && report.entitiesReason) {
      reportNotes.push({ key: 'entitiesUnavailable', tone: 'warn', body: report.entitiesReason });
    }
    if (report.truncated && view === 'table') {
      reportNotes.push({
        key: 'periodsTruncated',
        tone: 'warn',
        body: (
          <>
            The projection runs past what {granularityWord.toLowerCase()} columns can show:{' '}
            <strong>{report.periodsOmitted} further period{report.periodsOmitted !== 1 ? 's' : ''}</strong> fall
            beyond the axis. Their cash is included in the <strong>After</strong> column and in every total —
            switch to a coarser resolution, or narrow the date range, to see it laid out.
          </>
        ),
      });
    }
    if (report.debtAvailable === false) {
      reportNotes.push({
        key: 'debtUnavailable',
        tone: 'warn',
        body: `${report.debtReason} A levered position’s return to equity is overstated until it can be netted.`,
      });
    }
    // WHAT THE UNREALIZED RATE DOES NOT SPEAK FOR. It qualifies a KPI and a
    // table column, and it is also the rate the chart's unrealized curve is
    // drawn from. The engine words it (`rollupIrr`), so the counts here and
    // the ⚠ on the "All deals" row can never disagree.
    if (report.irr?.unrealized?.caveat) {
      reportNotes.push({
        key: 'unrealizedCaveat',
        tone: 'warn',
        body: (
          <>
            <strong>Unrealized IRR is incomplete.</strong> {report.irr.unrealized.caveat}
            {report.irr.unrealized.valuedItems === 0
              ? ' Nothing on the book has a current carrying value, so the figure is not worth reading.'
              : ` It is solved over the ${report.irr.unrealized.valuedItems} capital item${report.irr.unrealized.valuedItems !== 1 ? 's' : ''} that ${report.irr.unrealized.valuedItems !== 1 ? 'do' : 'does'} have one.`}
          </>
        ),
      });
    }
    // AN UNAVAILABLE COMPARISON IS NOT SILENT. The rates are still the numbers
    // people came for, so it never costs the report — but a chart with no
    // market line on it looks like a chart whose market line is zero, and the
    // reason names the fix, which is a different one in each case: sync the
    // FRED SP500 series, record a valuation mark, or accept that capital
    // deployed before the index's licensed history cannot be priced at all.
    if (report.marketComparison && report.marketComparison.available === false && view === 'chart') {
      reportNotes.push({
        key: 'marketUnavailable',
        tone: 'info',
        body: report.marketComparison.reason || (report.benchmark && report.benchmark.reason),
      });
    }
    // The ledger going missing looks exactly like a book that has not spent
    // anything, so its absence is never silent.
    if (report.actualsAvailable === false && report.actualsReason) {
      reportNotes.push({
        key: 'actualsUnavailable',
        tone: report.actualsIncluded ? 'warn' : 'info',
        body: report.actualsReason,
      });
    }
    if (report.coverage.actualsOnly > 0) {
      reportNotes.push({
        key: 'actualsOnly',
        tone: 'info',
        body: (
          <>
            <strong>{report.coverage.actualsOnly} deal{report.coverage.actualsOnly !== 1 ? 's' : ''}</strong> show
            realized Sage cash but nothing forward — no budget, no loan schedule, no capital schedule and no
            valuation with an exit date. Their columns stop at today. Record a valuation mark with a planned sale
            date, a capital schedule, an R/E budget, or — on a note receivable — its rate and payment schedule
            segments, to project one past it.
          </>
        ),
      });
    }
    if (unavailableSources.length > 0) {
      reportNotes.push({
        key: 'unavailableSources',
        tone: 'info',
        body: unavailableSources.map(([k, v]) => <div key={k}>{v.reason}</div>),
      });
    }
    if ((report.coverage.bySource?.none || 0) > 0 && !hideZeroRows && view === 'table') {
      reportNotes.push({
        key: 'noSourceRows',
        tone: 'info',
        body: (
          <>
            <strong>{report.coverage.bySource.none} capital item{report.coverage.bySource.none !== 1 ? 's' : ''}</strong> have
            nothing at all — no budget, no loan schedule, no capital schedule, no valuation with an exit date, and no
            Sage location or account tagged, so no ledger either. They are shown as empty rows rather than zeros, marked with
            a <strong>*</strong> pointing at the reason below; they contribute nothing to the totals.
          </>
        ),
      });
    }
    // The period columns follow the chosen measure while Before / After and
    // Total are always net, so a non-net view has to say so — a reader adding
    // a row across would otherwise find it does not tie.
    if (measure !== 'net' && view === 'table') {
      reportNotes.push({
        key: 'measureNotNet',
        tone: 'info',
        body: (
          <>
            The period columns show <strong>{(MEASURES.find(m => m.key === measure) || {}).label}</strong>.
            The <strong>Before</strong>, <strong>After</strong> and <strong>Total net</strong> columns are always
            net, so a row will not add across in this view — switch to Net to reconcile one.
          </>
        ),
      });
    }
  }
  // ROW-LEVEL NOTES JOIN THE SAME LIST — see the comment above `reportNotes`.
  // Appended rather than interleaved so the report's own caveats (which the
  // reader is used to seeing first) still lead; `rowNotes` is itself already
  // grouped, so this never adds one entry per deal.
  for (const n of rowNotes) reportNotes.push(n);

  return (
    <div>
      <PageHeader
        icon="ledger"
        title="Cash Flow Projection"
        subtitle={report && report.available
          ? `${report.coverage.projected} of ${report.coverage.deals} deals projected · ${report.periods.length} ${granularityWord.toLowerCase()} period${report.periods.length !== 1 ? 's' : ''}${report.window?.firstDate ? ` from ${fmt.date(report.window.firstDate)}` : ''}`
          : 'Realized Sage cash and projected cash flow from each deal’s budget, capital schedule and valuation'}
        actions={
          <>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 10, display: 'flex', color: B.textLt, pointerEvents: 'none' }}><Icon name="search" size={15} /></span>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search deal, capital item…"
                style={{ padding: '8px 12px 8px 32px', borderRadius: radius.md, border: `1px solid ${B.border}`, fontSize: font.base, minWidth: 220, fontFamily: fonts.sans, background: B.surface, outline: 'none' }} />
            </div>
            <Btn size="sm" variant="ghost" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>
            {/* ONE EXPORT, AND IT IS A WORKBOOK. There were two buttons — a
                client-side CSV of the matrix and the .xlsx of the flows
                underneath it — which read as duplicates and gave somebody two
                files that had to be reconciled by hand. The matrix is now a
                SHEET in that workbook, with its row totals and grand total as
                formulas, so the table and the series it was solved from open
                together and tie.

                WHAT THE MOVE COST, stated because the button cannot: the search
                box never reached the server, so the file is the whole run
                rather than what is on screen. Reproducing the match on the API
                would be a second implementation of it, and a workbook narrowed
                by a filter nobody can see in the file is worse than one that
                says it covers everything. The status filter and the date window
                DO reach the server and are sent. */}
            <Btn size="sm" disabled={!report || !report.available || xlsxBusy}
              onClick={runXlsxExport}
              title="The report as a workbook: the matrix, every dated flow behind it, the terminals, the per-deal index comparison and every rate — with the arithmetic as live formulas. The whole run; the search box does not narrow it."
            ><Icon name="download" size={14} /> {xlsxBusy ? 'Building…' : 'Export to Excel'}</Btn>
          </>
        }
      />

      {/* A FAILED EXPORT IS SHOWN, NOT SWALLOWED. The download happens in
          another tab via a SAS link, so a rejected request produces no visible
          effect at all — the button simply stops saying "Building…" and
          nothing arrives, which reads as a broken product rather than as an
          error. Rendered verbatim from the API, which names the reason (a
          pre-migration schema, or a run with nothing to export). */}
      {xlsxError && (
        <div style={{ marginBottom: space.md }}>
          <ErrorMsg msg={xlsxError} />
        </div>
      )}

      {/* THE TOOLBAR AND THE FILTER PANEL — see the filter-bar header above
          FilterGroup for the split. The toolbar: which view, then the controls
          that shape THAT view and nothing else (a control that does nothing to
          what is on screen is worse than an absent one; state is KEPT across a
          switch so the table comes back as it was left). The panel: what is in
          the run, behind a Filters button whose header always names what is in
          force. The search box stays in the page header on purpose: it narrows
          only the rendered table, is client-side, and shapes neither the chart
          nor the workbook — stated on the export button rather than left to be
          discovered from a file wider than the table above it. */}
      <div role="region" aria-label="Report filters and view" style={{
        display: 'flex', flexDirection: 'column', gap: space.sm,
        padding: `${space.sm}px ${space.md}px`, marginBottom: space.lg,
        border: `1px solid ${B.divider}`, borderRadius: radius.lg, background: B.surfaceMuted,
      }}>
        {/* THE TOOLBAR — HOW THE RUN IS SHOWN, specific to the view it
            shapes. The four that shape the matrix's columns and cells are not
            rendered beside the chart (it has no periods, no measure and no
            rows), and the forward market assumption is not rendered beside
            the table (it moves nothing the matrix shows). */}
        <FilterTier label={view === 'chart' ? 'vs S&P 500' : 'Table'}>
          <Segmented options={VIEWS} value={view} onChange={setView} ariaLabel="Table or chart" />
          {view === 'chart' && (
            <ForwardGrowthControl
              value={growthPct} onChange={setGrowthPct}
              report={report} user={user}
              /* Saving the firm default changes what THIS report was computed
                 under, so the run is re-made rather than the label being patched
                 locally — a chart drawn at one rate under a caption naming
                 another is the exact disagreement this control exists to end. */
              onSaved={reload}
            />
          )}
          {view === 'table' && (
            <>
              <FilterGroup label="Periods">
                <Segmented options={GRANULARITIES} value={granularity} onChange={setGranularity} ariaLabel="Period resolution" />
              </FilterGroup>
              <FilterGroup label="Figure">
                <Segmented options={MEASURES} value={measure} onChange={setMeasure} ariaLabel="Figure shown" />
              </FilterGroup>
              <FilterGroup label="Window" hint="The axis the table is laid out on. Money outside it is still counted, in the Before and After columns and in every total.">
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: font.sm, color: B.textMd }}>
                  <label htmlFor="cf-from">From</label>
                  <input id="cf-from" type="date" value={from} onChange={e => setFrom(e.target.value)}
                    style={{ padding: '6px 9px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.sm, fontFamily: fonts.sans, background: B.surface, outline: 'none' }} />
                  <label htmlFor="cf-to">to</label>
                  <input id="cf-to" type="date" value={to} onChange={e => setTo(e.target.value)}
                    style={{ padding: '6px 9px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.sm, fontFamily: fonts.sans, background: B.surface, outline: 'none' }} />
                </div>
              </FilterGroup>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: font.sm, color: B.textMd, cursor: 'pointer' }}>
                <input type="checkbox" checked={hideZeroRows} onChange={e => setHideZeroRows(e.target.checked)} />
                <span title="Hide every row whose cells are all zero in this view — including one whose flows net out to nothing within a period. The portfolio total is unchanged: an empty row contributes nothing to it.">
                  Hide 0 rows
                </span>
              </label>
            </>
          )}
        </FilterTier>

        {/* THE FILTER PANEL — WHAT IS IN THE RUN. Every control here reaches
            the server: which deals (status), which capital items (types, hold
            types) and which deal-level Sage entities ride along decide every
            total, both IRRs, the chart's curves and the workbook alike, so
            the panel is the same in BOTH views. Checkboxes throughout (see
            CheckGroup). A hold type narrows real estate ONLY, so its boxes
            appear only while R/E is in and the book holds any; the entity
            list appears only once the server has sent one (a pre-182
            database cannot). The header beside the button names every
            filter in force, each with its own clear, whether or not the
            panel is open. */}
        <FilterDisclosure
          open={filtersOpen}
          onToggle={() => setFiltersOpen((v) => !v)}
          count={activeFilterItems.length}
          summary={<ActiveFilters items={activeFilterItems} onClearAll={clearFilters} />}
        >
          <PanelRow label="Status" hint="Which deals are in the run, by status. Reaches the server: it moves both views and the workbook.">
            <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Deal status"
              style={{ padding: '4px 9px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.sm, fontFamily: fonts.sans, background: B.surface, outline: 'none' }}>
              <option value="All">All statuses</option>
              {['Prospect', 'Active', 'Exited', 'Dead'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </PanelRow>
          {/* THE VINTAGE. A cohort filter, not the table's window: a deal whose
              first realized Sage outflow is before this date is out of every
              figure — the rates, both views, the workbook — so the run answers
              "how has capital deployed since X done". A deal with no realized
              capital yet is kept (nothing deployed has no date to be before),
              and the server's caveat says so. The chart's own start anchor
              follows this date when it is set. */}
          <PanelRow label="Deployed from" hint="Keep only deals whose first capital deployment (earliest realized Sage outflow) is on or after this date. Deals deployed earlier leave the run entirely; the caveat below names them.">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: font.sm, color: B.textMd }}>
              <input id="cf-deployed-from" type="date" value={deployedFrom} onChange={e => setDeployedFrom(e.target.value)}
                aria-label="Keep only deals first deployed on or after"
                style={{ padding: '4px 9px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.sm, fontFamily: fonts.sans, background: B.surface, outline: 'none' }} />
              {deployedFrom && (
                <button type="button" onClick={() => setDeployedFrom('')} style={{ ...linkBtn, padding: '2px 4px' }}>Clear</button>
              )}
            </div>
          </PanelRow>
          <CheckGroup
            label="Capital types"
            ariaLabel="Capital types included in the run"
            options={DETAIL_TYPES.map((t) => ({ value: t, label: shortOf(t) }))}
            excluded={excludedTypes}
            onToggle={toggleType}
            colorFor={colorOf}
          />
          {/* THE DEAL-LEVEL SAGE ENTITIES — the holding company's own postings
              and borrowing, attached to the deal and to no capital item (see
              api/src/functions/cashFlowReport.js, loadEntityActuals). A LIST,
              one box per attached entity, each naming its code and its deal;
              not a fifth capital type. It is an ADDITION to the capital items,
              so every box may be unticked (`allowNone`) — that is the "leave
              the entities out" switch — where the last capital type may not. */}
          {ownerOffered && (
            <CheckDropdown
              label="Owner entity"
              noun="owners"
              hint="The Sage entity at the top of the location hierarchy a deal's capital items are tagged into. Untick one to leave its deals out of the run."
              ariaLabel="Owner entities whose deals are in the run"
              options={ownerChoices}
              excluded={excludedOwners}
              onToggle={toggleOwner}
              onSetAll={setAllOwners}
              colorFor={() => colorOf(ENTITY_TYPE)}
              extras={ownerExtras}
              extrasHeading="On the Sage roster, owning no deal in this run"
            />
          )}
          {reIncluded && holdOptions.length > 0 && (
            <CheckDropdown
              label="R/E hold type"
              noun="hold types"
              hint="Narrows real estate only: a note or a position has no hold type and is unaffected."
              ariaLabel="Real estate hold types included in the run"
              options={holdChoices.map((h) => ({ value: h, label: h === NO_HOLD_TYPE ? HOLD_TYPE_NONE_LABEL : h }))}
              excluded={excludedHoldTypes}
              onToggle={toggleHoldType}
              colorFor={() => colorOf('R/E')}
            />
          )}
        </FilterDisclosure>
      </div>

      {error ? <ErrorMsg msg={error} onRetry={reload} /> : loading ? <Spinner /> : !report ? null : !report.available ? (
        <Caveat tone="warn">{report.reason}</Caveat>
      ) : (
        <>
          <Card style={{ padding: space.lg, marginBottom: space.lg }}>
            <div style={{ display: 'flex', gap: space.xxl, flexWrap: 'wrap' }}>
              <Kpi label="Cash in" value={fmt.currency(report.totals.inflow || 0)}
                hint="Distributions and sale proceeds" tone={B.green} />
              <Kpi label="Cash out" value={fmt.currency(report.totals.outflow || 0)}
                hint="Capital calls and cost draws" tone={B.red} />
              <Kpi label="Net" value={fmt.currency(report.totals.net || 0)}
                hint={`${report.totals.eventCount} flow${report.totals.eventCount !== 1 ? 's' : ''}`}
                tone={(report.totals.net || 0) < 0 ? B.red : B.green} />
              {/* How much of that Net has actually happened. Without it the
                  headline reads as a forecast in full, when on this book most of
                  it is history. */}
              {report.actualsAvailable && report.coverage.withActuals > 0 && (
                <Kpi label="Of which realized" value={fmt.currency((report.actuals && report.actuals.net) || 0)}
                  hint={`Sage postings through ${report.asOf ? fmt.date(report.asOf) : 'today'}`}
                  tone={B.textMd} />
              )}
              {/* THE BOOK'S TWO RATES. Each carries the sentence that says what
                  it assumed, and a dash carries the reason it could not be
                  solved — a blank rate invites the reader to assume the book is
                  at zero rather than unanswerable. */}
              {/* THE STRIP MUST NOT STATE A RATE MORE CONFIDENTLY THAN THE
                  ENGINE DID. `rollupIrr` returns a caveat whenever the book's
                  capital is charged against carrying values that do not cover
                  it — items with realized cash and no mark, and notes left out
                  altogether. The table row carries the same `*` (see NoteMark);
                  the KPI printed the bare number under "if every position sold
                  today at its carrying value", which is the sentence the caveat
                  exists to qualify. The mark is here and the sentence itself
                  is collected below, where there is room to read it. */}
              <Kpi label="Unrealized IRR"
                value={(
                  <>
                    {formatRate(report.irr?.unrealized?.irr) || '—'}
                    {report.irr?.unrealized?.caveat && <NoteMark onClick={openRowNotes} />}
                  </>
                )}
                hint={report.irr?.unrealized?.irr != null
                  ? (report.irr?.unrealized?.caveat
                    ? 'If every position sold today at its carrying value — but see below'
                    : 'If every position sold today at its carrying value')
                  : (report.irr?.unrealized?.reason || 'Cannot be solved')}
                tone={rateSign(report.irr?.unrealized?.irr) === 'down' ? B.red
                  : rateSign(report.irr?.unrealized?.irr) === 'up' ? B.green : B.textMd} />
              <Kpi label="Projected IRR"
                value={formatRate(report.irr?.projected?.irr) || '—'}
                hint={report.irr?.projected?.irr != null
                  ? 'If every position is held to plan'
                  : (report.irr?.projected?.reason || 'Cannot be solved')}
                tone={rateSign(report.irr?.projected?.irr) === 'down' ? B.red
                  : rateSign(report.irr?.projected?.irr) === 'up' ? B.green : B.textMd} />
              <Kpi label="From" value={report.periods.length ? report.periods[0].label : '—'}
                hint={report.window?.firstDate ? fmt.date(report.window.firstDate) : null} />
              <Kpi label="Through" value={report.periods.length ? report.periods[report.periods.length - 1].label : '—'}
                hint={report.window?.lastDate ? fmt.date(report.window.lastDate) : null} />
              <div style={{ minWidth: 190 }}>
                <div style={{ fontSize: font.xs, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: weight.semibold }}>Figures from</div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 }}>
                  {[...PROJECTION_SOURCES, 'actual'].map(s => {
                    const n = report.coverage.bySource?.[s] || 0;
                    if (!n) return null;
                    const m = SOURCE_LABELS[s];
                    return <span key={s} title={m.hint}><Badge color={m.color}>{n} {m.label.toLowerCase()}</Badge></span>;
                  })}
                  {(report.coverage.bySource?.none || 0) > 0 && (
                    <Badge color={B.textMd}>{report.coverage.bySource.none} with nothing at all</Badge>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Every caveat that used to sit here — the narrowing filters, an
              incomplete rate, a truncated axis, a missing ledger or market
              line — is now collected into `reportNotes` above and rendered
              collapsed at the FOOT of the report (see ReportNotesDisclosure),
              out of the way of the KPIs and the table/chart. The export
              result stays here: it is feedback on the button just pressed,
              not a standing note about the report's data, and a reader who
              just clicked Export should not have to open a collapsed panel to
              learn whether it worked. */}
          {exportMsg && (
            <Caveat tone={exportMsg.tone === 'ok' ? 'info' : exportMsg.tone}>
              {exportMsg.text}
              <button type="button" onClick={() => setExportMsg(null)}
                style={{
                  marginLeft: 8, background: 'none', border: 'none', padding: 0,
                  color: B.textLt, cursor: 'pointer', fontSize: font.sm,
                  fontFamily: fonts.sans, textDecoration: 'underline',
                }}>Dismiss</button>
            </Caveat>
          )}

          {/* ONE VIEW AT A TIME (see VIEWS). The toggle at the top is what
              makes the chart discoverable — which is the job its old position
              above the matrix was doing, and it did it by pushing the report
              itself below the fold. */}
          {view === 'chart' ? (
            <MarketComparisonCard report={report} />
          ) : (
            <CashFlowMatrix
              report={report}
              rows={rows}
              measure={measure}
              hideZeroRows={hideZeroRows}
              showBefore={showBefore}
              showAfter={showAfter}
              onDealClick={onDealClick}
              onNoteClick={openRowNotes}
            />
          )}

          <div style={{ marginTop: space.md, fontSize: font.xs, color: B.textLt, lineHeight: 1.6, maxWidth: 940 }}>
            Cash is shown from the investor’s standpoint: capital calls and cost draws are outflows, distributions and
            sale proceeds are inflows. Periods are calendar periods — quarters run Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec.
            {report.actualsIncluded && report.actualsAvailable && (
              <> Columns headed <strong>actual</strong> are realized cash from the Sage general ledger, on every capital
              item tagged to a Sage location or account — whether or not anything has been modelled for it. Where an item
              has both, the two are blended: what happened up to {report.asOf ? fmt.date(report.asOf) : 'today'}, what is
              planned after it, so a draw that was both forecast and made is counted once.</>
            )}{' '}
            Each capital item is projected forward from exactly one model: its R/E budget if it has one, otherwise a
            note receivable’s own loan terms, otherwise its capital schedule, otherwise a sale at its current valuation.
            A valuation is only added as an exit where nothing else models one, and it is always net of debt outstanding
            — so the same money is never counted twice. A note is projected from its schedule segments by the same
            engine that generates the payment records the borrower is billed from: the principal advanced, every
            scheduled payment through maturity, and whatever principal is still outstanding when it gets there — which
            on an interest-only note is the whole of it.
            An expected sale date that has already passed is kept and flagged rather than dropped; if that asset has in
            fact sold, retire its mark or the sale will show beside the realized proceeds.{' '}
            {report.entitiesIncluded && report.entitiesAvailable && (
              <> Rows badged <strong>Entity</strong> are the deal’s own Sage entities — the holding company attached to the
              deal rather than to a capital item — carrying its postings and borrowing as realized cash; where such an
              entity carries debt and projects nothing, the debt is repaid out of the deal’s mark-driven sale.</>
            )}{' '}
            <strong>Hover a deal or capital item name</strong> for how its figures were arrived at — the model, its
            components, and the mark it is carried at.{' '}
            The two rates are solved from the flows on the row through the same solver the Valuation tab and the
            Capital Schedule tab use: <strong>Unrealized IRR</strong> is realized Sage cash then a sale today at the
            carrying value, <strong>Projected IRR</strong> is realized Sage cash then every remaining projected flow
            including the exit. A deal’s rate is solved over the union of its capital items’ flows, never averaged from
            theirs — a rate is not additive. A dash means the rate cannot be solved from what is recorded, which is
            not the same as zero; hover it for the reason.{' '}
            A <strong>*</strong> beside a row or a rate marks a note about it — why it isn’t projected, whose money a
            Sage entity is, what a rate credits no gain on — collected below rather than left floating on the row;
            click it (or open “Notes about this report”) to read it.
          </div>

          {/* Every caveat about this run, collapsed at the true foot of the
              report — see `reportNotes` above and ReportNotesDisclosure. The
              ref is what lets a `*` on any row or KPI scroll the reader here
              (see `openRowNotes`). */}
          <div ref={notesRef}>
            <ReportNotesDisclosure
              items={reportNotes}
              open={notesOpen}
              onToggle={() => setNotesOpen((v) => !v)}
            />
          </div>
        </>
      )}
    </div>
  );
};
